import { EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { ProCard, ProDescriptions, ProTable } from '@ant-design/pro-components';
import { history, useAccess } from '@umijs/max';
import type { FormInstance } from 'antd';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  message,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CrmList } from '@/components/CrmList';
import QuietEmpty from '@/components/QuietEmpty';
import Empty from '@/components/QuietEmpty';
import {
  ActivityPanel,
  ActivityPanelFollowUps,
} from '@/modules/zy-crm/components/activity-panel/export';
import { AiDetailPanel } from '@/modules/zy-crm/components/ai-detail-panel';
import { AttachmentPanel } from '@/modules/zy-crm/components/attachment-panel';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import { BusinessTripRelationPanel } from '@/modules/zy-crm/components/business-trip-relation-panel';
import {
  type CalendarEventFormValues,
  calendarEventFormDefaultValues,
  createCalendarRelationFormValue,
  normalizeCalendarRelationPayload,
} from '@/modules/zy-crm/components/calendar-event-form';
import { ContactReferenceLink } from '@/modules/zy-crm/components/contact-detail-drawer';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { ContractCenterSyncAction } from '@/modules/zy-crm/components/contract-center-sync-action';
import { ContractEditModal } from '@/modules/zy-crm/components/contract-edit-modal';
import { EntityTagEditor } from '@/modules/zy-crm/components/crm-tags';
import { CrmTaskRelationPanel } from '@/modules/zy-crm/components/crm-task-relation-panel';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import { DetailNavigation } from '@/modules/zy-crm/components/detail-navigation';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import { FollowUpCreateModal } from '@/modules/zy-crm/components/follow-up-create-modal';
import { InvoiceEditModal } from '@/modules/zy-crm/components/invoice-edit-modal';
import {
  buildInvoiceTableColumns,
  type InvoiceTableRecord,
} from '@/modules/zy-crm/components/invoice-table';
import { LeadReferenceLink } from '@/modules/zy-crm/components/lead-detail-drawer';
import { LeadEditModal } from '@/modules/zy-crm/components/lead-edit-modal';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  OpportunityDetailDrawerProvider,
  OpportunityReferenceLink,
} from '@/modules/zy-crm/components/opportunity-detail-drawer';
import { OpportunityEditModal } from '@/modules/zy-crm/components/opportunity-edit-modal';
import { QuoteEditorModal } from '@/modules/zy-crm/components/quote-editor-modal';
import { ReceiptPanel } from '@/modules/zy-crm/components/receipt-panel';
import { ReferralSection as UnifiedReferralSection } from '@/modules/zy-crm/components/referral-section';
import { TeamMembersPanel } from '@/modules/zy-crm/components/team-members-panel';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import type { CrmProColumnCompat } from '@/modules/zy-crm/types/pro-table-compat';
import type { RelationCreateContext } from '@/modules/zy-crm/types/relation-create-context';
import { confirmContactDelete } from '@/modules/zy-crm/utils/confirm-contact-delete';
import { FOLLOW_UP_STATUS_ENUM } from '@/modules/zy-crm/utils/constants';
import {
  canEditContract,
  resolveContractDisplayStatusLabel,
} from '@/modules/zy-crm/utils/contract-center';
import {
  type CrmScopedViewMode,
  resolveCrmScopedViewModeFromPathname,
} from '@/modules/zy-crm/utils/crm-view';
import {
  defaultDetailSection,
  detailSectionLabel,
  normalizeDetailSections,
} from '@/modules/zy-crm/utils/detail-navigation';
import {
  formatCrmDate,
  resolveRelationText,
  toApiDateTime,
} from '@/modules/zy-crm/utils/display';
import {
  canActOnRecord,
  canReadRecord,
} from '@/modules/zy-crm/utils/record-permission';
import {
  fetchAreaOptions,
  fetchContactOptions,
  fetchCustomerLevelOptions,
  fetchCustomerOptions,
  fetchLeadOptions,
  fetchOpportunityOptions,
  fetchSourceChannelOptions,
  mergeOptions,
  type RelationOption,
  resolveRelationLabel,
} from '@/modules/zy-crm/utils/relation-options';
import { hasActionAccess } from '@/permissions/helpers';
import {
  createCalendarEvent,
  updateCalendarEvent,
} from '@/services/bff/calendar.api';
import { queryContacts, updateContact } from '@/services/bff/contact.api';
import {
  createCustomerAddress,
  deleteCustomerAddress,
  queryCustomerDrawerProfile,
  updateCustomerAddress,
} from '@/services/bff/customer.api';
import { deleteLead, queryLeads } from '@/services/bff/lead.api';
import {
  queryOpportunities,
  updateOpportunity,
} from '@/services/bff/opportunity.api';
import {
  createReferral,
  deleteReferral,
  updateReferral,
} from '@/services/bff/referral.api';
import type { CalendarEventItem } from '@/services/bff/types/calendar';
import type { ContactItem } from '@/services/bff/types/contact';
import type {
  CreateCustomerAddressDto,
  CustomerAddressItem,
  CustomerDrawerActivity,
  CustomerDrawerContactItem,
  CustomerDrawerContractItem,
  CustomerDrawerFollowUpItem,
  CustomerDrawerInvoiceItem,
  CustomerDrawerOpportunityItem,
  CustomerDrawerProductItem,
  CustomerDrawerProfile,
  CustomerDrawerQuoteItem,
  CustomerDrawerReceiptScheduleItem,
  CustomerDrawerRelatedPage,
  CustomerDrawerRelationship,
  CustomerDrawerSection,
  CustomerDrawerSectionKey,
  CustomerItem,
  UpdateCustomerAddressDto,
} from '@/services/bff/types/customer';
import type { LeadItem } from '@/services/bff/types/lead';
import type { ReferralItem } from '@/services/bff/types/referral';
import { OPPORTUNITY_CONVERTED_TO_LEAD } from '@/services/crmRecordEvents';
import { queryTaxonomyTreeByIdentifier } from '@/services/mp/taxonomy';
import { createAppRequest } from '@/services/request';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import { flattenTaxonomyToOptions } from '@/utils/taxonomy';
import { queryCustomerOpportunityPage } from './customer-opportunity-page';
import { hasCustomerDrawerProfileData } from './profile-placeholder';
import styles from './style.module.less';

const { Text, Title, Paragraph } = Typography;
const _appRequest = createAppRequest();
const _MemberPickerField = MemberPicker as unknown as React.FC<any>;
const CUSTOMER_FOLLOW_UP_SCOPE_PAGE_SIZE = 100;

type CustomerDrawerOpenInput = {
  viewMode?: CrmScopedViewMode;
  customerUuid?: string;
  customerName?: ReactNode;
};

type CustomerDetailDrawerContextValue = {
  openCustomer: (input: CustomerDrawerOpenInput) => void;
};

type CustomerDetailDrawerProviderProps = {
  children: ReactNode;
};

type CustomerReferenceLinkProps = {
  viewMode?: CrmScopedViewMode;
  customerUuid?: string;
  customerName?: ReactNode;
  fallbackName?: ReactNode;
  emptyText?: ReactNode;
  canViewDetail?: boolean;
  style?: CSSProperties;
};

type SectionPanelProps = {
  title: string;
  hint?: string;
  extra?: ReactNode;
  children: ReactNode;
};

type CustomerDrawerLookups = {
  areaOptions: RelationOption[];
  industryOptions: RelationOption[];
  csrcIndustryOptions: RelationOption[];
  customerLevelOptions: RelationOption[];
  sourceChannelOptions: RelationOption[];
  memberNameMap: Record<string, string>;
};

const TAXONOMY_INDUSTRY = 'TonghuashunIndustryTaxonomy';
const TAXONOMY_CSRC_INDUSTRY =
  'ChinaSecuritiesRegulatoryCommissionIndustryTaxonomy';

type AddressEditorFormValues = CreateCustomerAddressDto;

type ScheduleEditorState = {
  mode: 'create' | 'edit';
  item?: CalendarEventItem;
};

type AddressEditorState = {
  mode: 'create' | 'edit';
  item?: CustomerAddressItem;
};

type RelatedEditorState = {
  kind: 'followUp';
  record: CustomerDrawerFollowUpItem;
};

type DrawerQuoteEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; recordUuid: string };

type DrawerContractEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; recordUuid: string };

type DrawerInvoiceEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; recordUuid: string };

type DrawerOpportunityEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; recordUuid: string };

type CustomerDetailViewMode = 'drawer' | 'page';

type CustomerNextAction = {
  key: string;
  title: string;
  description: string;
  tone: 'urgent' | 'attention' | 'focus';
};

type CustomerHealthSignal = {
  key: string;
  label: string;
  value: string;
  tone: 'danger' | 'warning' | 'healthy' | 'info';
};

type CustomerReferralEditorValues = {
  incomingReferrerType?: ReferralItem['referrerType'];
  incomingReferrerUuid?: string;
  incomingReferralTime?: Dayjs;
  incomingIsPrimary?: boolean;
  incomingProfitShareRatio?: number;
  incomingRemark?: string;
};

type IncomingReferralFormValues = Pick<
  CustomerReferralEditorValues,
  | 'incomingReferrerType'
  | 'incomingReferrerUuid'
  | 'incomingReferralTime'
  | 'incomingIsPrimary'
  | 'incomingProfitShareRatio'
  | 'incomingRemark'
>;

type OutgoingReferralFormValues = {
  targetType?: ReferralItem['targetType'];
  targetUuid?: string;
  referralTime?: Dayjs;
  remark?: string;
};

type ReferralInlineCreateState = {
  entity: 'customer' | 'contact';
  form: 'incoming' | 'outgoing';
};

const CustomerDetailDrawerContext =
  createContext<CustomerDetailDrawerContextValue | null>(null);

const EMPTY_PAGE: CustomerDrawerRelatedPage<never> = {
  list: [],
  total: 0,
};

const SECTION_LABELS: Array<{
  key: CustomerDrawerSectionKey;
  label: string;
  enabled: boolean;
}> = [
  { key: 'overview360', label: '360视图', enabled: true },
  // 客户资料与附件。
  { key: 'details', label: '详细资料', enabled: true },
  { key: 'attachments', label: '附件', enabled: true },
  // 客户相关人员与关系。
  { key: 'contacts', label: '联系人', enabled: true },
  { key: 'teamMembers', label: '协作成员', enabled: true },
  { key: 'referrals', label: '介绍关系', enabled: true },
  // 销售业务按推进顺序排列。
  { key: 'leads', label: '线索', enabled: true },
  { key: 'opportunities', label: '商机', enabled: true },
  { key: 'quotes', label: '报价单', enabled: true },
  { key: 'contracts', label: '合同', enabled: true },
  { key: 'receipts', label: '回款', enabled: true },
  { key: 'invoices', label: '发票', enabled: true },
  // 活动与执行记录相邻，跟进记录紧接活动。
  { key: 'activities', label: '活动', enabled: true },
  { key: 'followUps', label: '跟进记录', enabled: true },
  { key: 'tasks', label: '任务', enabled: true },
  { key: 'businessTrips', label: '相关出差', enabled: true },
  { key: 'expenses', label: '费用', enabled: false },
  { key: 'amountRatio', label: '金额比例设置', enabled: false },
  { key: 'operationLogs', label: '操作记录', enabled: false },
];

const SECTION_UNSUPPORTED_COPY: Partial<
  Record<CustomerDrawerSectionKey, string>
> = {
  expenses: '当前暂无费用数据。',
  amountRatio: '当前暂无金额比例数据。',
  operationLogs: '当前暂无操作记录。',
};

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeOptionalText = (value: unknown) =>
  normalizeText(value) || undefined;

const dedupeStrings = (values: string[]) => Array.from(new Set(values));

const toReferralOption = (
  uuid?: string,
  name?: string,
): RelationOption | null => {
  const value = normalizeText(uuid);
  if (!value) {
    return null;
  }
  return {
    value,
    label: normalizeText(name) || value,
  };
};

const isDuplicateOutgoingReferral = ({
  currentRecordUuid,
  targetType,
  targetUuid,
  items,
}: {
  currentRecordUuid?: string;
  targetType?: ReferralItem['targetType'];
  targetUuid?: string;
  items: ReferralItem[];
}) => {
  const nextTargetType = normalizeText(targetType);
  const nextTargetUuid = normalizeText(targetUuid);
  if (!nextTargetType || !nextTargetUuid) {
    return false;
  }
  return items.some(
    (item) =>
      normalizeText(item.targetType) === nextTargetType &&
      normalizeText(item.targetUuid) === nextTargetUuid &&
      normalizeText(item.uuid) !== normalizeText(currentRecordUuid),
  );
};

const buildIncomingCustomerReferrals = (
  incoming: CustomerDrawerRelatedPage<ReferralItem>,
) => incoming.list.filter((item) => item.targetType === 'customer');

const buildOutgoingReferrals = (
  outgoing: CustomerDrawerRelatedPage<ReferralItem>,
) => outgoing.list.filter((item) => item.referrerType === 'customer');

const buildIncomingReferralFormValues = (
  record?: ReferralItem,
): IncomingReferralFormValues => ({
  incomingReferrerType: record?.referrerType,
  incomingReferrerUuid: normalizeOptionalText(record?.referrerUuid),
  incomingReferralTime: record?.referralTime
    ? dayjs(record.referralTime)
    : undefined,
  incomingIsPrimary: Boolean(record?.isPrimary),
  incomingProfitShareRatio:
    record?.profitShareRatio === undefined
      ? undefined
      : Number(record.profitShareRatio),
  incomingRemark: normalizeOptionalText(record?.remark),
});

const validateCustomerReferralValues = ({
  customerUuid,
  incomingReferrerUuid,
  outgoingTargetType,
  outgoingTargetUuid,
}: {
  customerUuid: string;
  incomingReferrerUuid?: string;
  outgoingTargetType?: ReferralItem['targetType'];
  outgoingTargetUuid?: string;
}) => {
  const currentCustomerUuid = normalizeText(customerUuid);
  if (!currentCustomerUuid) {
    throw new Error('缺少当前客户信息，无法保存介绍关系');
  }

  if (
    incomingReferrerUuid &&
    normalizeText(incomingReferrerUuid) === currentCustomerUuid
  ) {
    throw new Error('谁介绍了当前客户不能选择当前客户自己');
  }

  if (
    outgoingTargetType === 'customer' &&
    normalizeText(outgoingTargetUuid) === currentCustomerUuid
  ) {
    throw new Error('当前客户介绍出去的对象不能选择当前客户自己');
  }
};

export async function syncCustomerOutgoingReferrals({
  customerUuid,
  outgoingCustomerUuids,
  initialOutgoingItems,
}: {
  customerUuid: string;
  outgoingCustomerUuids: string[];
  initialOutgoingItems?: ReferralItem[];
}) {
  const currentCustomerUuid = normalizeText(customerUuid);
  const nextOutgoingCustomerUuids = dedupeStrings(
    outgoingCustomerUuids.map((item) => normalizeText(item)).filter(Boolean),
  );
  const previousItems = (initialOutgoingItems || []).filter(
    (item) =>
      item.referrerType === 'customer' &&
      item.targetType === 'customer' &&
      normalizeText(item.referrerUuid) === currentCustomerUuid,
  );

  const previousByTarget = new Map(
    previousItems.map((item) => [normalizeText(item.targetUuid), item]),
  );
  const nextTargetSet = new Set(nextOutgoingCustomerUuids);

  await Promise.all(
    previousItems
      .filter((item) => !nextTargetSet.has(normalizeText(item.targetUuid)))
      .filter((item) => item.uuid)
      .map((item) => deleteReferral(item.uuid)),
  );

  await Promise.all(
    nextOutgoingCustomerUuids.map((targetUuid) => {
      const existing = previousByTarget.get(targetUuid);
      const payload = {
        referrerType: 'customer' as const,
        referrerUuid: currentCustomerUuid,
        targetType: 'customer' as const,
        targetUuid,
      };
      return existing?.uuid
        ? updateReferral(existing.uuid, payload)
        : createReferral(payload);
    }),
  );
}

const _toPickerValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const toIsoValue = (value?: Dayjs | null) => toApiDateTime(value);

type MemberListItem = {
  memberUuid?: string;
  userUuid?: string;
  name?: string;
  nickname?: string;
  username?: string;
  phone?: string;
};

const _toEditorMemberOptions = (members: MemberListItem[]) =>
  members
    .map((member) => {
      const value = normalizeText(member?.memberUuid);
      if (!value) {
        return null;
      }
      const label = normalizeText(
        member?.name || member?.nickname || member?.username || member?.phone,
      );
      return { value, label: label || value };
    })
    .filter(Boolean) as Array<{ label: string; value: string }>;

const _UNIFIED_CARD_HEAD_STYLE = {
  minHeight: 36,
  paddingBlock: 4,
} as const;

const _UNIFIED_CARD_BODY_STYLE = {
  padding: 12,
} as const;

const cleanTechnicalText = (value: unknown, technicalValue?: unknown) => {
  const text = normalizeText(value);
  const rawValue = normalizeText(technicalValue);
  if (!text) return '';
  if (rawValue && text === rawValue) return '';
  return text;
};

const formatMoney = (value?: string | number | null) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0.00';
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toTimestamp = (value?: string | null) => {
  if (!value) return 0;
  const time = dayjs(value).valueOf();
  return Number.isFinite(time) ? time : 0;
};

const daysFromNow = (value?: string | null) => {
  if (!value) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  return parsed.startOf('day').diff(dayjs().startOf('day'), 'day');
};

const soonestBy = <T,>(list: T[], pickTime: (item: T) => string | undefined) =>
  [...list]
    .map((item) => ({
      item,
      time: toTimestamp(pickTime(item)),
    }))
    .filter((item) => item.time > 0)
    .sort((a, b) => a.time - b.time)[0]?.item;

const pickCustomerName = (customer?: CustomerItem, fallback?: ReactNode) => {
  const directName =
    normalizeText(customer?.name) ||
    normalizeText(
      (customer as CustomerItem & { customerName?: string })?.customerName,
    ) ||
    normalizeText(
      (customer as CustomerItem & { customerDisplayName?: string })
        ?.customerDisplayName,
    );
  if (directName) return directName;
  if (fallback) return fallback;
  return customer?.uuid || '未命名客户';
};

const _pickMemberLabel = (item?: MemberListItem) =>
  normalizeText(item?.name) ||
  normalizeText(item?.nickname) ||
  normalizeText(item?.username) ||
  normalizeText(item?.phone);

const resolveMemberText = ({
  memberUuid,
  directName,
  memberNameMap = {},
  emptyText = '-',
}: {
  memberUuid?: string;
  directName?: unknown;
  memberNameMap?: Record<string, string>;
  emptyText?: string;
}) =>
  resolveRelationText({
    value: memberUuid,
    directName: cleanTechnicalText(directName, memberUuid),
    nameMap: memberNameMap,
    emptyText,
  });

const ownerText = (
  customer?: CustomerItem,
  memberNameMap: Record<string, string> = {},
) =>
  resolveMemberText({
    memberUuid: customer?.ownerUserUuid,
    directName:
      customer?.ownerUserName ||
      customer?.ownerName ||
      customer?.ownerMemberName,
    memberNameMap,
  });

const buildReceiptScheduleSummary = (
  schedules: CustomerDrawerReceiptScheduleItem[] = [],
) => {
  const receivedTotal = schedules.reduce(
    (sum, item) => sum + Number(item.receivedAmount || 0),
    0,
  );
  const occupyingTotal = schedules.reduce(
    (sum, item) => sum + Number(item.occupyingAmount || 0),
    0,
  );
  const availableTotal = schedules.reduce((sum, item) => {
    const explicit = Number(item.availableAmount ?? '');
    if (Number.isFinite(explicit)) {
      return sum + explicit;
    }
    return (
      sum +
      Number(item.price || 0) -
      Number(item.receivedAmount || 0) -
      Number(item.occupyingAmount || 0)
    );
  }, 0);
  return {
    receivedTotal,
    occupyingTotal,
    availableTotal,
  };
};

const buildHealthSignals = (
  profile?: CustomerDrawerProfile,
): CustomerHealthSignal[] => {
  if (!profile) return [];
  const summary = profile.summary;
  const related = profile.related;
  const signals: CustomerHealthSignal[] = [];
  const unfollowedDays = summary.unfollowedDays ?? null;
  const scheduleSummary = buildReceiptScheduleSummary(
    related.receiptSchedules?.list || [],
  );
  const availableAmount = scheduleSummary.availableTotal;
  const overdueAmount = Number(summary.overdueScheduleAmount || 0);
  const expiringContracts = related.contracts.list.filter((item) => {
    const diff = daysFromNow(item.endTime);
    return diff !== null && diff >= 0 && diff <= 30;
  }).length;

  if (unfollowedDays !== null) {
    signals.push({
      key: 'silence',
      label: '跟进状态',
      value:
        unfollowedDays >= 14
          ? `已静默 ${unfollowedDays} 天`
          : unfollowedDays >= 7
            ? `${unfollowedDays} 天未跟进`
            : `近 ${unfollowedDays} 天有跟进`,
      tone:
        unfollowedDays >= 14
          ? 'danger'
          : unfollowedDays >= 7
            ? 'warning'
            : 'healthy',
    });
  }

  signals.push({
    key: 'receipt',
    label: '回款压力',
    value:
      overdueAmount > 0
        ? `逾期 ${formatMoney(overdueAmount)} 元`
        : availableAmount > 0
          ? `可回款余额 ${formatMoney(availableAmount)} 元`
          : '可回款余额 0 元',
    tone:
      overdueAmount > 0
        ? 'danger'
        : availableAmount > 0
          ? 'warning'
          : 'healthy',
  });

  signals.push({
    key: 'contract-expire',
    label: '合同风险',
    value: expiringContracts
      ? `${expiringContracts} 份合同 30 天内到期`
      : '近期无到期合同',
    tone: expiringContracts ? 'warning' : 'healthy',
  });

  if (!related.contacts.total) {
    signals.push({
      key: 'contacts',
      label: '联系人覆盖',
      value: '0 位联系人',
      tone: 'warning',
    });
  }

  return signals;
};

const buildNextActions = (
  profile?: CustomerDrawerProfile,
): CustomerNextAction[] => {
  if (!profile) return [];
  const summary = profile.summary;
  const related = profile.related;
  const actions: CustomerNextAction[] = [];
  const scheduleSummary = buildReceiptScheduleSummary(
    related.receiptSchedules?.list || [],
  );

  const nextContactDays = daysFromNow(profile.customer.contactNextTime);
  if (nextContactDays !== null) {
    actions.push({
      key: 'next-contact',
      title:
        nextContactDays < 0 ? '先补一次客户联系' : '优先处理下一次客户联系',
      description:
        nextContactDays < 0
          ? `计划联系时间已过 ${Math.abs(
              nextContactDays,
            )} 天，建议先同步最新跟进结果`
          : nextContactDays <= 3
            ? `下次联系时间在 ${nextContactDays} 天内，建议提前准备沟通重点`
            : '已安排下次联系，可继续围绕当前节点推进',
      tone: nextContactDays < 0 ? 'urgent' : 'focus',
    });
  }

  const availableAmount = scheduleSummary.availableTotal;
  const overdueAmount = Number(summary.overdueScheduleAmount || 0);
  if (availableAmount > 0) {
    actions.push({
      key: 'receipt-schedule',
      title: overdueAmount > 0 ? '优先处理逾期回款' : '核对近期回款计划',
      description:
        overdueAmount > 0
          ? `当前已有 ${formatMoney(
              overdueAmount,
            )} 元逾期未回，建议先催款并确认责任人`
          : `当前仍有 ${formatMoney(
              availableAmount,
            )} 元可回款余额，建议确认计划和跟催节奏`,
      tone: 'urgent',
    });
  }

  const expiringContract = soonestBy(
    related.contracts.list.filter((item) => {
      const diff = daysFromNow(item.endTime);
      return diff !== null && diff >= 0 && diff <= 30;
    }),
    (item) => item.endTime,
  );
  if (expiringContract) {
    actions.push({
      key: 'contract-expire',
      title: '关注临近到期合同',
      description: `${
        expiringContract.name || expiringContract.code || '合同'
      } 即将到期，建议准备续签或回款确认`,
      tone: 'attention',
    });
  }

  const expectedQuote = soonestBy(
    related.quotes.list.filter((item) => {
      const diff = daysFromNow(item.expectedDealDate);
      return diff !== null && diff >= 0 && diff <= 30;
    }),
    (item) => item.expectedDealDate,
  );
  if (expectedQuote) {
    actions.push({
      key: 'expected-deal',
      title: '跟紧预计成交报价',
      description: `${
        expectedQuote.name || expectedQuote.code || '报价单'
      } 已接近预计成交时间，建议确认成交阻碍和资源需求`,
      tone: 'focus',
    });
  }

  if (!actions.length) {
    actions.push({
      key: 'steady',
      title: '当前客户状态平稳',
      description: '没有识别到紧急风险，建议先回顾最近动态并补齐下一次推进动作',
      tone: 'focus',
    });
  }

  return actions.slice(0, 3);
};

const buildBusinessBoard = (profile?: CustomerDrawerProfile) => {
  if (!profile) return [];
  const scheduleSummary = buildReceiptScheduleSummary(
    profile.related.receiptSchedules?.list || [],
  );
  return [
    {
      key: 'opportunity',
      title: '商机与预计成交',
      value: `${profile.summary.opportunityCount} 条 / ${formatMoney(
        profile.summary.opportunityTotalAmount,
      )} 元`,
      hint: `已赢单 ${profile.summary.wonOpportunityCount} 条`,
    },
    {
      key: 'contract',
      title: '合同与到期提醒',
      value: `${profile.summary.contractCount} 份 / ${formatMoney(
        profile.summary.contractTotalAmount,
      )} 元`,
      hint: '关注 30 天内到期合同和续签节奏',
    },
    {
      key: 'receipt',
      title: '回款与回款计划',
      value: `${formatMoney(profile.summary.receiptTotalAmount)} 元 / 完成率 ${Number(
        profile.summary.receiptCompletionRate || 0,
      ).toFixed(2)}%`,
      hint: `已回款金额 ${formatMoney(
        scheduleSummary.receivedTotal,
      )} 元，占用金额 ${formatMoney(
        scheduleSummary.occupyingTotal,
      )} 元，可回款余额 ${formatMoney(
        scheduleSummary.availableTotal,
      )} 元，逾期 ${formatMoney(profile.summary.overdueScheduleAmount)} 元`,
    },
    {
      key: 'invoice',
      title: '发票与单据状态',
      value: `${profile.related.invoices.total} 条 / ${formatMoney(
        profile.summary.invoiceTotalAmount,
      )} 元`,
      hint: `开票覆盖率 ${Number(
        profile.summary.invoiceCoverageRate || 0,
      ).toFixed(2)}%`,
    },
  ];
};

const boolTag = (value?: boolean) => (
  <Tag color={value ? 'green' : 'default'}>{value ? '是' : '否'}</Tag>
);

const statusTag = (label: string, value?: string | number | boolean | null) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return (
    <Tag color="blue">
      {label} {String(value)}
    </Tag>
  );
};

const relationshipRoleLabelMap: Record<string, string> = {
  decision_maker: '决策人',
  influencer: '影响者',
  user: '使用者',
  purchaser: '采购',
  technical_gatekeeper: '技术把关',
  champion: '推动者',
  other: '其他',
};

const relationshipTemperatureMap: Record<
  string,
  { label: string; color: string }
> = {
  hot: { label: '高温', color: 'red' },
  warm: { label: '稳定', color: 'gold' },
  cold: { label: '降温', color: 'blue' },
  unknown: { label: '待观察', color: 'default' },
};

const activityLevelMap: Record<string, { label: string; color: string }> = {
  active: { label: '7天内活跃', color: 'green' },
  normal: { label: '30天内有联系', color: 'blue' },
  low: { label: '超过30天未联系', color: 'orange' },
  risk: { label: '超过60天未联系', color: 'red' },
  unknown: { label: '尚未联系', color: 'default' },
};

const descriptionColumns = {
  xs: 1,
  sm: 2,
} as const;

export function useCustomerDetailDrawer() {
  const context = useContext(CustomerDetailDrawerContext);
  return (
    context || {
      openCustomer: ({ customerUuid }: CustomerDrawerOpenInput) => {
        if (customerUuid) {
          history.push(`/zy-crm/customer/${customerUuid}`);
        }
      },
    }
  );
}

export function CustomerDetailDrawerProvider({
  children,
}: CustomerDetailDrawerProviderProps) {
  const [selectedViewMode, setSelectedViewMode] = useState<CrmScopedViewMode>();
  const [open, setOpen] = useState(false);
  const [customerUuid, setCustomerUuid] = useState<string>();
  const [fallbackName, setFallbackName] = useState<ReactNode>();
  const [profile, setProfile] = useState<CustomerDrawerProfile>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const latestProfileRequestRef = useRef(0);
  const pageViewMode =
    selectedViewMode ||
    resolveCrmScopedViewModeFromPathname(
      (history.location?.pathname || '') + (history.location?.search || ''),
      'customers',
      { allowPool: true },
    );

  const openCustomer = useCallback((input: CustomerDrawerOpenInput) => {
    const uuid = normalizeText(input.customerUuid);
    if (!uuid) {
      message.warning('该记录缺少客户 UUID，无法打开客户详情');
      return;
    }
    setProfile(undefined);
    setError(undefined);
    setCustomerUuid(uuid);
    setSelectedViewMode(input.viewMode);
    setFallbackName(input.customerName);
    setOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const loadProfile = useCallback(async () => {
    if (!customerUuid || !open) {
      return;
    }
    const requestId = latestProfileRequestRef.current + 1;
    latestProfileRequestRef.current = requestId;
    setLoading(true);
    setError(undefined);
    try {
      const data = await queryCustomerDrawerProfile(customerUuid, {
        viewMode: pageViewMode,
      });
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      if (hasCustomerDrawerProfileData(data)) {
        setProfile(data);
        setError(undefined);
        return;
      }
      throw new Error('客户详情暂无有效数据');
    } catch (e) {
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      setError(e instanceof Error ? e : new Error('客户详情加载失败'));
      setProfile(undefined);
    } finally {
      if (requestId === latestProfileRequestRef.current) {
        setLoading(false);
      }
    }
  }, [customerUuid, open, pageViewMode]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const value = useMemo(() => ({ openCustomer }), [openCustomer]);

  return (
    <CustomerDetailDrawerContext.Provider value={value}>
      {children}
      <OpportunityDetailDrawerProvider>
        <CustomerDetailDrawerView
          open={open}
          loading={loading}
          error={error}
          profile={profile}
          fallbackName={fallbackName}
          pageViewMode={pageViewMode}
          onClose={closeDrawer}
          onRefresh={() => void loadProfile()}
        />
      </OpportunityDetailDrawerProvider>
    </CustomerDetailDrawerContext.Provider>
  );
}

export function CustomerReferenceLink({
  viewMode,
  customerUuid,
  customerName,
  fallbackName,
  emptyText = '-',
  canViewDetail = true,
  style,
}: CustomerReferenceLinkProps) {
  const { openCustomer } = useCustomerDetailDrawer();
  const label = customerName || fallbackName || emptyText;
  if (!normalizeText(customerUuid) || canViewDetail === false) {
    const content = (
      <Text type="secondary" style={style}>
        {label}
      </Text>
    );
    if (normalizeText(customerUuid) && canViewDetail === false) {
      return <Tooltip title="无详情权限">{content}</Tooltip>;
    }
    return content;
  }
  return (
    <Button
      type="link"
      className={styles.customerLink}
      style={style}
      onClick={() =>
        openCustomer({ customerUuid, customerName: label, viewMode })
      }
    >
      {label}
    </Button>
  );
}

export function CustomerDetailDrawerView({
  open,
  mode = 'drawer',
  loading,
  error,
  profile,
  fallbackName,
  pageViewMode,
  onClose,
  onRefresh,
}: {
  open: boolean;
  mode?: CustomerDetailViewMode;
  loading: boolean;
  error?: Error;
  profile?: CustomerDrawerProfile;
  fallbackName?: ReactNode;
  pageViewMode: CrmScopedViewMode;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [activeSection, setActiveSection] =
    useState<CustomerDrawerSectionKey>('overview360');
  const previousCustomerUuidRef = useRef<string | undefined>(undefined);

  const isPageMode = mode === 'page';

  const customer = profile?.customer;
  const displayName = pickCustomerName(customer, fallbackName);
  const sections = useMemo(() => resolveSections(profile), [profile]);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  const visibleSections = useMemo(
    () => sections.filter((section) => section.enabled),
    [sections],
  );
  const [areaOptions, setAreaOptions] = useState<RelationOption[]>([]);
  const [industryOptions, setIndustryOptions] = useState<RelationOption[]>([]);
  const [csrcIndustryOptions, setCsrcIndustryOptions] = useState<
    RelationOption[]
  >([]);
  const [customerLevelOptions, setCustomerLevelOptions] = useState<
    RelationOption[]
  >([]);
  const [sourceChannelOptions, setSourceChannelOptions] = useState<
    RelationOption[]
  >([]);
  const [memberNameMap, setMemberNameMap] = useState<Record<string, string>>(
    {},
  );
  const [attachmentCount, setAttachmentCount] = useState<number | undefined>();
  const [followUpCount, setFollowUpCount] = useState<{
    customerUuid: string;
    total: number;
  }>();
  const [leadPage, setLeadPage] = useState<CustomerDrawerRelatedPage<LeadItem>>(
    EMPTY_PAGE as CustomerDrawerRelatedPage<LeadItem>,
  );
  const [leadLoading, setLeadLoading] = useState(false);
  const [opportunityPage, setOpportunityPage] = useState<
    CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>
  >(EMPTY_PAGE as CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>);
  const [opportunityLoading, setOpportunityLoading] = useState(false);
  const [leadEditOpen, setLeadEditOpen] = useState(false);
  const [editingLeadUuid, setEditingLeadUuid] = useState<string>();
  const latestLeadRequestRef = useRef(0);
  const latestOpportunityRequestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      previousCustomerUuidRef.current = undefined;
      return;
    }

    const currentCustomerUuid = normalizeText(profile?.customer?.uuid);
    if (!currentCustomerUuid) {
      return;
    }

    const sectionList = visibleSections;
    const fallbackSection =
      (defaultDetailSection(
        'customer',
        sectionList,
      ) as CustomerDrawerSectionKey) || 'overview360';
    const sectionKeys = new Set(
      sectionList.map((section) => section.key as CustomerDrawerSectionKey),
    );
    const isCustomerChanged =
      previousCustomerUuidRef.current !== currentCustomerUuid;

    if (isCustomerChanged) {
      previousCustomerUuidRef.current = currentCustomerUuid;
      setActiveSection(fallbackSection);
      return;
    }

    if (sectionKeys.size === 0) {
      return;
    }

    if (!sectionKeys.has(activeSection)) {
      setActiveSection(fallbackSection);
    }
  }, [activeSection, open, profile?.customer?.uuid, visibleSections]);

  const {
    memberNameMap: sharedMemberNameMap,
    searchMembers,
    ensureMemberValue,
  } = useMemberDirectory();

  useEffect(() => {
    let disposed = false;

    if (!open || !profile) {
      setAreaOptions([]);
      setIndustryOptions([]);
      setCsrcIndustryOptions([]);
      setCustomerLevelOptions([]);
      setSourceChannelOptions([]);
      setMemberNameMap({});
      return () => {
        disposed = true;
      };
    }

    const memberUuids = Array.from(
      new Set(
        [
          profile.customer.ownerUserUuid,
          ...((profile.related.teamMembers.list || []).map(
            (item) => item.memberUuid,
          ) || []),
        ]
          .map((item) => normalizeText(item))
          .filter(Boolean),
      ),
    );

    Promise.all([
      fetchAreaOptions().catch(() => []),
      queryTaxonomyTreeByIdentifier(TAXONOMY_INDUSTRY)
        .then((tree) => flattenTaxonomyToOptions(tree.tags))
        .catch(() => []),
      queryTaxonomyTreeByIdentifier(TAXONOMY_CSRC_INDUSTRY)
        .then((tree) => flattenTaxonomyToOptions(tree.tags))
        .catch(() => []),
      fetchCustomerLevelOptions().catch(() => []),
      fetchSourceChannelOptions().catch(() => []),
      memberUuids.length
        ? Promise.all(
            memberUuids.map((memberUuid) =>
              ensureMemberValue(memberUuid).catch(() => undefined),
            ),
          ).then(() => sharedMemberNameMap)
        : Promise.resolve(sharedMemberNameMap),
    ]).then(
      ([
        areas,
        industries,
        csrcIndustries,
        customerLevels,
        sourceChannels,
        members,
      ]) => {
        if (disposed) return;
        setAreaOptions(areas);
        setIndustryOptions(industries);
        setCsrcIndustryOptions(csrcIndustries);
        setCustomerLevelOptions(customerLevels);
        setSourceChannelOptions(sourceChannels);
        setMemberNameMap({ ...sharedMemberNameMap, ...members });
      },
    );

    return () => {
      disposed = true;
    };
  }, [ensureMemberValue, open, profile, sharedMemberNameMap]);

  const loadCustomerLeads = useCallback(async () => {
    const currentCustomerUuid = normalizeText(profile?.customer?.uuid);
    if (!open || !currentCustomerUuid) {
      setLeadPage(EMPTY_PAGE as CustomerDrawerRelatedPage<LeadItem>);
      return;
    }
    const requestId = latestLeadRequestRef.current + 1;
    latestLeadRequestRef.current = requestId;
    setLeadLoading(true);
    try {
      const data = await queryLeads({
        customerUuid: currentCustomerUuid,
        page: 1,
        pageSize: 20,
      });
      if (requestId !== latestLeadRequestRef.current) {
        return;
      }
      setLeadPage({
        list: data.list || [],
        total: data.total || 0,
      });
    } catch (error) {
      if (requestId !== latestLeadRequestRef.current) {
        return;
      }
      setLeadPage(EMPTY_PAGE as CustomerDrawerRelatedPage<LeadItem>);
      const nextError =
        error instanceof Error ? error : new Error('线索列表加载失败');
      message.error(nextError.message);
    } finally {
      if (requestId === latestLeadRequestRef.current) {
        setLeadLoading(false);
      }
    }
  }, [open, profile?.customer?.uuid]);

  useEffect(() => {
    void loadCustomerLeads();
  }, [loadCustomerLeads]);

  const loadCustomerOpportunities = useCallback(async () => {
    const currentCustomerUuid = normalizeText(profile?.customer?.uuid);
    if (!open || !currentCustomerUuid) {
      setOpportunityPage(
        EMPTY_PAGE as CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>,
      );
      return;
    }
    const requestId = latestOpportunityRequestRef.current + 1;
    latestOpportunityRequestRef.current = requestId;
    setOpportunityLoading(true);
    try {
      const data = await queryCustomerOpportunityPage(
        currentCustomerUuid,
        pageViewMode,
      );
      if (requestId !== latestOpportunityRequestRef.current) {
        return;
      }
      setOpportunityPage(data);
    } catch (error) {
      if (requestId !== latestOpportunityRequestRef.current) {
        return;
      }
      setOpportunityPage(
        EMPTY_PAGE as CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>,
      );
      const nextError =
        error instanceof Error ? error : new Error('商机列表加载失败');
      message.error(nextError.message);
    } finally {
      if (requestId === latestOpportunityRequestRef.current) {
        setOpportunityLoading(false);
      }
    }
  }, [open, pageViewMode, profile?.customer?.uuid]);

  useEffect(() => {
    const refresh = () => {
      void loadCustomerOpportunities();
      void loadCustomerLeads();
    };
    window.addEventListener(OPPORTUNITY_CONVERTED_TO_LEAD, refresh);
    return () =>
      window.removeEventListener(OPPORTUNITY_CONVERTED_TO_LEAD, refresh);
  }, [loadCustomerOpportunities, loadCustomerLeads]);

  useEffect(() => {
    void loadCustomerOpportunities();
  }, [loadCustomerOpportunities]);

  const drawerLookups = useMemo<CustomerDrawerLookups>(
    () => ({
      areaOptions,
      industryOptions,
      csrcIndustryOptions,
      customerLevelOptions,
      sourceChannelOptions,
      memberNameMap,
    }),
    [
      areaOptions,
      industryOptions,
      csrcIndustryOptions,
      customerLevelOptions,
      sourceChannelOptions,
      memberNameMap,
    ],
  );
  const healthSignals = useMemo(() => buildHealthSignals(profile), [profile]);
  const nextActions = useMemo(() => buildNextActions(profile), [profile]);
  const businessBoard = useMemo(() => buildBusinessBoard(profile), [profile]);
  const sectionsWithRelatedCounts = useMemo(
    () =>
      sections.map((section) =>
        section.key === 'followUps'
          ? {
              ...section,
              count:
                (followUpCount?.customerUuid === profile?.customer.uuid
                  ? followUpCount?.total
                  : undefined) ??
                profile?.related?.followUps?.total ??
                section.count ??
                profile?.summary?.followUpCount ??
                0,
            }
          : section.key === 'leads'
            ? { ...section, count: leadPage.total }
            : section.key === 'opportunities'
              ? { ...section, count: opportunityPage.total }
              : section.key === 'attachments'
                ? {
                    ...section,
                    count:
                      attachmentCount ??
                      section.count ??
                      profile?.related?.attachments?.total ??
                      0,
                  }
                : section,
      ),
    [
      attachmentCount,
      followUpCount,
      profile?.customer.uuid,
      profile?.related?.followUps?.total,
      profile?.summary?.followUpCount,
      leadPage.total,
      opportunityPage.total,
      profile?.related?.attachments?.total,
      sections,
    ],
  );
  const visibleSectionsWithRelatedCounts = useMemo(
    () => sectionsWithRelatedCounts.filter((section) => section.enabled),
    [sectionsWithRelatedCounts],
  );

  const activeSectionMeta =
    visibleSectionsWithRelatedCounts.find(
      (section) => section.key === activeSection,
    ) || visibleSectionsWithRelatedCounts[0];
  const relationContext = useMemo<RelationCreateContext>(
    () => ({
      customerUuid: customer?.uuid,
      customerName: normalizeText(customer?.name),
    }),
    [customer?.name, customer?.uuid],
  );
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [contactAssociationOpen, setContactAssociationOpen] = useState(false);
  const [contactAssociationOptions, setContactAssociationOptions] = useState<
    RelationOption[]
  >([]);
  const [selectedContactUuids, setSelectedContactUuids] = useState<string[]>(
    [],
  );
  const [contactAssociationLoading, setContactAssociationLoading] =
    useState(false);
  const [contactAssociationSubmitting, setContactAssociationSubmitting] =
    useState(false);
  const [opportunityEditor, setOpportunityEditor] =
    useState<DrawerOpportunityEditorState | null>(null);
  const [linkOpportunityOpen, setLinkOpportunityOpen] = useState(false);
  const [linkOpportunitySubmitting, setLinkOpportunitySubmitting] =
    useState(false);
  const [editingContactUuid, setEditingContactUuid] = useState<string>();
  const [quoteEditor, setQuoteEditor] = useState<DrawerQuoteEditorState | null>(
    null,
  );
  const [contractEditor, setContractEditor] =
    useState<DrawerContractEditorState | null>(null);
  const [invoiceEditor, setInvoiceEditor] =
    useState<DrawerInvoiceEditorState | null>(null);
  const [relatedEditor, setRelatedEditor] = useState<RelatedEditorState | null>(
    null,
  );
  const [relatedMemberOptions, setRelatedMemberOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [relatedMemberLoading, setRelatedMemberLoading] = useState(false);
  const [relatedMemberLoadFailed, setRelatedMemberLoadFailed] = useState(false);
  const [scheduleEditor, setScheduleEditor] =
    useState<ScheduleEditorState | null>(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleForm] = Form.useForm<CalendarEventFormValues>();
  const [addressEditor, setAddressEditor] = useState<AddressEditorState | null>(
    null,
  );
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [addressForm] = Form.useForm<AddressEditorFormValues>();
  const [quickFollowUpOpen, setQuickFollowUpOpen] = useState(false);
  const [quickFollowUpSubmitting, _setQuickFollowUpSubmitting] =
    useState(false);
  const editingFollowUp =
    relatedEditor?.kind === 'followUp' ? relatedEditor.record : undefined;

  const closeQuoteEditor = useCallback(() => {
    setQuoteEditor(null);
  }, []);

  const closeContractEditor = useCallback(() => {
    setContractEditor(null);
  }, []);

  const closeInvoiceEditor = useCallback(() => {
    setInvoiceEditor(null);
  }, []);

  const openLeadCreate = useCallback(() => {
    setEditingLeadUuid(undefined);
    setLeadEditOpen(true);
  }, []);

  const openLeadEdit = useCallback((record: LeadItem) => {
    setEditingLeadUuid(record.uuid);
    setLeadEditOpen(true);
  }, []);

  const openAddressCreate = useCallback(() => {
    addressForm.resetFields();
    addressForm.setFieldsValue({
      addressType: 'office',
    });
    setAddressEditor({ mode: 'create' });
  }, [addressForm]);

  const openAddressEdit = useCallback(
    (record: CustomerAddressItem) => {
      addressForm.resetFields();
      addressForm.setFieldsValue({
        addressType: record.addressType,
        postalCode: record.postalCode,
        contactUuid: record.contactUuid,
        detailAddress: record.detailAddress,
        remark: record.remark,
      });
      setAddressEditor({ mode: 'edit', item: record });
    },
    [addressForm],
  );

  const closeAddressEditor = useCallback(() => {
    setAddressEditor(null);
    addressForm.resetFields();
  }, [addressForm]);

  const handleAddressDelete = useCallback(
    (record: CustomerAddressItem) => {
      if (!customer?.uuid) {
        return;
      }
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除地址',
        content: `确认删除“${record.detailAddress}”吗`,
        okText: '删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteCustomerAddress(customer.uuid, record.uuid);
          message.success('地址删除成功');
          onRefresh();
        },
      });
    },
    [customer?.uuid, onRefresh],
  );

  const submitAddressEditor = useCallback(async () => {
    if (!customer?.uuid || !addressEditor) {
      return;
    }
    const values = await addressForm.validateFields();
    const payload: CreateCustomerAddressDto | UpdateCustomerAddressDto = {
      addressType: values.addressType,
      postalCode: normalizeOptionalText(values.postalCode),
      contactUuid: normalizeOptionalText(values.contactUuid),
      detailAddress: normalizeText(values.detailAddress),
      remark: normalizeOptionalText(values.remark),
    };
    setAddressSubmitting(true);
    try {
      if (addressEditor.mode === 'edit' && addressEditor.item?.uuid) {
        await updateCustomerAddress(
          customer.uuid,
          addressEditor.item.uuid,
          payload,
        );
        message.success('地址更新成功');
      } else {
        await createCustomerAddress(
          customer.uuid,
          payload as CreateCustomerAddressDto,
        );
        message.success('地址新增成功');
      }
      closeAddressEditor();
      onRefresh();
    } finally {
      setAddressSubmitting(false);
    }
  }, [
    addressEditor,
    addressForm,
    closeAddressEditor,
    customer?.uuid,
    onRefresh,
  ]);

  const handleLeadDelete = useCallback(
    (record: LeadItem) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除线索',
        content: `确认删除线索“${record.name || record.uuid}”吗`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          await deleteLead(record.uuid);
          message.success('线索删除成功');
          await loadCustomerLeads();
          onRefresh();
        },
      });
    },
    [loadCustomerLeads, onRefresh],
  );

  const openRelatedEditor = useCallback((next: RelatedEditorState) => {
    setRelatedEditor(next);
  }, []);

  const openContactEdit = useCallback((record: CustomerDrawerContactItem) => {
    setEditingContactUuid(record.uuid);
    setContactCreateOpen(false);
  }, []);

  const loadContactAssociationOptions = useCallback(
    async (keyword?: string) => {
      setContactAssociationLoading(true);
      try {
        const options = await fetchContactOptions(keyword);
        const available = options.filter(
          (option) => !String(option.raw?.customerUuid || '').trim(),
        );
        setContactAssociationOptions(available);
      } catch {
        message.error('联系人列表加载失败，请稍后重试');
      } finally {
        setContactAssociationLoading(false);
      }
    },
    [],
  );

  const openContactAssociation = useCallback(() => {
    setSelectedContactUuids([]);
    setContactAssociationOpen(true);
    void loadContactAssociationOptions();
  }, [loadContactAssociationOptions]);

  const submitContactAssociation = useCallback(async () => {
    const customerUuid = customer?.uuid;
    if (!customerUuid || !selectedContactUuids.length) {
      message.warning('请至少选择一个联系人');
      return;
    }
    setContactAssociationSubmitting(true);
    try {
      await Promise.all(
        selectedContactUuids.map((contactUuid) =>
          updateContact(contactUuid, { customerUuid }),
        ),
      );
      message.success('联系人关联成功');
      setContactAssociationOpen(false);
      setSelectedContactUuids([]);
      onRefresh();
    } finally {
      setContactAssociationSubmitting(false);
    }
  }, [customer?.uuid, onRefresh, selectedContactUuids]);

  const [selectedLinkOpportunityUuid, setSelectedLinkOpportunityUuid] =
    useState<string>();
  const [linkOpportunityOptions, setLinkOpportunityOptions] = useState<
    RelationOption[]
  >([]);
  const [linkOpportunityLoading, setLinkOpportunityLoading] = useState(false);

  const loadLinkOpportunityOptions = useCallback(async (keyword?: string) => {
    setLinkOpportunityLoading(true);
    try {
      const available = await fetchOpportunityOptions(keyword);
      setLinkOpportunityOptions(available);
    } finally {
      setLinkOpportunityLoading(false);
    }
  }, []);

  const submitLinkOpportunity = useCallback(async () => {
    const customerUuid = customer?.uuid;
    if (!customerUuid || !selectedLinkOpportunityUuid) {
      message.warning('请选择一个商机');
      return;
    }
    setLinkOpportunitySubmitting(true);
    try {
      await updateOpportunity(selectedLinkOpportunityUuid, { customerUuid });
      message.success('商机关联成功');
      setLinkOpportunityOpen(false);
      setSelectedLinkOpportunityUuid(undefined);
      await loadCustomerOpportunities();
      onRefresh();
    } finally {
      setLinkOpportunitySubmitting(false);
    }
  }, [
    customer?.uuid,
    loadCustomerOpportunities,
    onRefresh,
    selectedLinkOpportunityUuid,
  ]);

  const openLinkOpportunity = useCallback(() => {
    setSelectedLinkOpportunityUuid(undefined);
    setLinkOpportunityOpen(true);
    void loadLinkOpportunityOptions();
  }, [loadLinkOpportunityOptions]);

  const loadRelatedMembers = useCallback(
    async (keyword?: string) => {
      setRelatedMemberLoading(true);
      try {
        const options = await searchMembers(keyword);
        setRelatedMemberOptions((prev) => {
          const map = new Map(prev.map((item) => [item.value, item]));
          options.forEach((item) => {
            map.set(item.value, item);
          });
          return Array.from(map.values());
        });
        setRelatedMemberLoadFailed(false);
        return options;
      } catch {
        setRelatedMemberLoadFailed(true);
        message.error('成员列表加载失败，请稍后重试');
        return [] as RelationOption[];
      } finally {
        setRelatedMemberLoading(false);
      }
    },
    [searchMembers],
  );

  const _closeRelatedEditor = useCallback(() => {
    setRelatedEditor(null);
  }, []);

  const openQuickFollowUp = useCallback(() => {
    setQuickFollowUpOpen(true);
  }, []);

  const openScheduleCreate = useCallback(() => {
    if (!customer?.uuid) {
      return;
    }
    scheduleForm.resetFields();
    scheduleForm.setFieldsValue(
      calendarEventFormDefaultValues({
        title: '',
        eventType: 'my_schedule',
        startTime: dayjs(),
        endTime: dayjs().add(1, 'hour'),
        allDay: false,
        ownerMemberUuid: normalizeOptionalText(customer?.ownerUserUuid),
        participantMemberUuids: [],
        relations: [createCalendarRelationFormValue('customer', customer.uuid)],
      }),
    );
    void loadRelatedMembers();
    setScheduleEditor({ mode: 'create' });
  }, [
    customer?.ownerUserUuid,
    customer?.uuid,
    loadRelatedMembers,
    scheduleForm,
  ]);

  const _openScheduleEdit = useCallback(async () => {
    message.info('请前往日程列表编辑相关日程');
  }, []);

  const closeScheduleEditor = useCallback(() => {
    setScheduleEditor(null);
    setScheduleSubmitting(false);
    scheduleForm.resetFields();
  }, [scheduleForm]);

  const submitScheduleEditor = useCallback(async () => {
    if (!customer?.uuid || !scheduleEditor) {
      return;
    }
    const values = await scheduleForm.validateFields();
    setScheduleSubmitting(true);
    try {
      const startTime = toApiDateTime(values.startTime);
      if (!startTime) {
        throw new Error('invalid start time');
      }
      const payload = {
        title: normalizeText(values.title),
        eventType: normalizeOptionalText(values.eventType) || 'my_schedule',
        startTime,
        endTime: toApiDateTime(values.endTime),
        allDay: Boolean(values.allDay),
        location: normalizeOptionalText(values.location),
        description: normalizeOptionalText(values.description),
        ownerMemberUuid: normalizeOptionalText(values.ownerMemberUuid),
        participantMemberUuids: Array.isArray(values.participantMemberUuids)
          ? (values.participantMemberUuids
              .map((item) => normalizeOptionalText(item))
              .filter(Boolean) as string[])
          : [],
        relations: normalizeCalendarRelationPayload(values.relations) || [
          {
            bizType: 'customer' as const,
            bizUuid: customer.uuid,
            relationRole: 'primary' as const,
          },
        ],
      };

      if (scheduleEditor.mode === 'edit' && scheduleEditor.item?.uuid) {
        await updateCalendarEvent(scheduleEditor.item.uuid, payload);
        message.success('日程已更新');
      } else {
        await createCalendarEvent(payload);
        message.success('日程已创建');
      }
      closeScheduleEditor();
      onRefresh();
    } catch (error) {
      console.error(error);
      message.error('保存失败，请稍后重试');
    } finally {
      setScheduleSubmitting(false);
    }
  }, [
    closeScheduleEditor,
    customer?.uuid,
    onRefresh,
    scheduleEditor,
    scheduleForm,
  ]);

  const activeSectionKey = activeSectionMeta?.key;

  const content = (
    <>
      <div
        className={isPageMode ? styles.pageShell : styles.shell}
        data-crm-shell={!isPageMode || undefined}
      >
        <DetailSurface className={styles.heroCard}>
          <section className={styles.hero} data-crm-hero>
            <div className={styles.heroMain}>
              <div className={styles.heroTopRow} data-crm-hero-row>
                <Space
                  wrap
                  align="center"
                  size={8}
                  className={styles.titleRow}
                  data-crm-title-row
                >
                  <Title level={2} className={styles.title} data-crm-title>
                    {displayName}
                  </Title>
                  {customer?.dealStatus ? <Tag color="blue">已成交</Tag> : null}
                  {!customer?.ownerUserUuid ? (
                    <Tag color="orange">公海客户</Tag>
                  ) : null}
                  {customer?.lockStatus ? <Tag color="red">已锁定</Tag> : null}
                </Space>
                <Space wrap size={[8, 8]} className={styles.heroActions}>
                  {isPageMode && customer ? (
                    <Button onClick={handleClose}>返回</Button>
                  ) : null}
                  {customer ? (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => setCustomerEditOpen(true)}
                    >
                      编辑
                    </Button>
                  ) : null}
                </Space>
              </div>
              <div className={styles.metaGrid} data-crm-meta>
                <MetaItem
                  label="负责人"
                  value={ownerText(customer, memberNameMap)}
                />
                <MetaItem label="手机号" value={customer?.mobile || '-'} />
                <MetaItem
                  label="最近联系"
                  value={formatCrmDate(customer?.contactLastTime, 'date')}
                />
                <MetaItem
                  label="下次联系"
                  value={formatCrmDate(customer?.contactNextTime, 'date')}
                />
              </div>
            </div>
          </section>
        </DetailSurface>

        {loading && !profile ? (
          <div className={styles.loading}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        ) : error ? (
          <DetailAccessError
            onClose={isPageMode ? handleClose : undefined}
            onRetry={onRefresh}
          />
        ) : profile ? (
          <div className={styles.workspace} data-crm-workspace>
            <aside className={styles.navPanel} data-crm-nav>
              <DetailSurface
                className={styles.navCard}
                data-crm-nav-surface
                title="工作视角"
              >
                <DetailNavigation
                  className={styles.navMenu}
                  selectedKeys={
                    activeSectionMeta?.key ? [activeSectionMeta.key] : []
                  }
                  items={visibleSectionsWithRelatedCounts.map((section) => ({
                    key: section.key,
                    label: (
                      <div className={styles.menuLabel}>
                        <span>{section.label}</span>
                        {typeof section.count === 'number' &&
                        section.count > 0 ? (
                          <Text type="secondary">{section.count}</Text>
                        ) : null}
                      </div>
                    ),
                  }))}
                  onClick={({ key }) =>
                    setActiveSection(key as CustomerDrawerSectionKey)
                  }
                />
              </DetailSurface>
            </aside>

            <main className={styles.mainPanel} data-crm-main>
              {renderSectionContent(
                activeSectionKey,
                profile,
                leadPage,
                leadLoading,
                opportunityPage,
                opportunityLoading,
                drawerLookups,
                businessBoard,
                nextActions,
                healthSignals,
                relationContext,
                () => setCustomerEditOpen(true),
                () => setOpportunityEditor({ mode: 'create' }),
                (record) => {
                  if (!record.uuid) {
                    message.warning('当前商机缺少 UUID，无法编辑');
                    return;
                  }
                  setOpportunityEditor({
                    mode: 'edit',
                    recordUuid: record.uuid,
                  });
                },
                openLinkOpportunity,
                openContactAssociation,
                openContactEdit,
                (record) => confirmContactDelete(record, onRefresh),
                openLeadCreate,
                openLeadEdit,
                handleLeadDelete,
                () => void loadCustomerLeads(),
                (state) => {
                  if (!state.record.uuid) {
                    message.warning('当前跟进记录缺少 UUID，无法编辑');
                    return;
                  }
                  openRelatedEditor(state);
                },
                () => setQuoteEditor({ mode: 'create' }),
                (record) => {
                  if (!record.uuid) {
                    message.warning('当前报价单缺少 UUID，无法编辑');
                    return;
                  }
                  setQuoteEditor({ mode: 'edit', recordUuid: record.uuid });
                },
                () => setContractEditor({ mode: 'create' }),
                (record) => {
                  if (!record.uuid) {
                    message.warning('当前合同缺少 UUID，无法编辑');
                    return;
                  }
                  setContractEditor({ mode: 'edit', recordUuid: record.uuid });
                },
                () => setInvoiceEditor({ mode: 'create' }),
                (record) => {
                  if (!record.uuid) {
                    message.warning('当前发票缺少 UUID，无法编辑');
                    return;
                  }
                  setInvoiceEditor({ mode: 'edit', recordUuid: record.uuid });
                },
                openQuickFollowUp,
                openScheduleCreate,
                scheduleForm,
                scheduleEditor,
                scheduleSubmitting,
                relatedMemberOptions,
                relatedMemberLoading,
                relatedMemberLoadFailed,
                loadRelatedMembers,
                closeScheduleEditor,
                submitScheduleEditor,
                openAddressCreate,
                openAddressEdit,
                handleAddressDelete,
                pageViewMode,
                onRefresh,
                (count: number) => setAttachmentCount(count),
                (total: number) =>
                  setFollowUpCount({
                    customerUuid: profile.customer.uuid,
                    total,
                  }),
              )}
            </main>

            <div className={styles.summaryPanel} data-crm-summary>
              <AiDetailPanel
                bizType="customer"
                bizUuid={customer?.uuid || ''}
                summaryCounts={profile.summary}
                initialSummary={profile.summaryPanel}
                activeTab="summary"
                contextTitle={profile.customer?.name}
              />
            </div>
          </div>
        ) : (
          <div className={styles.loading}>
            <Empty description={false} />
          </div>
        )}
      </div>
      <CustomerEditModal
        open={customerEditOpen}
        onOpenChange={setCustomerEditOpen}
        recordUuid={customer?.uuid}
        currentMemberUuid={null}
        context={undefined}
        onSuccess={() => {
          setCustomerEditOpen(false);
          onRefresh();
        }}
      />
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={Boolean(addressEditor)}
        title={addressEditor?.mode === 'edit' ? '编辑地址' : '新增地址'}
        onCancel={closeAddressEditor}
        onOk={() => void submitAddressEditor()}
        confirmLoading={addressSubmitting}
        destroyOnHidden
      >
        <Form form={addressForm} layout="vertical">
          <Form.Item
            name="addressType"
            label="地址类型"
            rules={[{ required: true, message: '请选择地址类型' }]}
          >
            <Select
              options={[
                { label: '办公地址', value: 'office' },
                { label: '仓储地址', value: 'warehouse' },
                { label: '注册地址', value: 'registered' },
                { label: '门店地址', value: 'store' },
                { label: '家庭地址', value: 'home' },
                { label: '其它地址', value: 'other' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="contactUuid"
            label="联系人"
            rules={[{ required: true, message: '请选择联系人' }]}
          >
            <Select
              showSearch
              placeholder="请选择联系人"
              options={(profile?.related?.contacts?.list || []).map((item) => ({
                label: item.name || item.mobile || item.uuid,
                value: item.uuid,
              }))}
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="postalCode" label="邮政编码">
            <Input placeholder="请输入邮政编码" maxLength={32} />
          </Form.Item>
          <Form.Item
            name="detailAddress"
            label="详细地址"
            rules={[{ required: true, message: '请输入详细地址' }]}
          >
            <Input.TextArea
              placeholder="请输入详细地址"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea
              placeholder="请输入备注"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={contactAssociationOpen}
        title="关联已有联系人"
        okText="确认关联"
        cancelText="取消"
        confirmLoading={contactAssociationSubmitting}
        okButtonProps={{ disabled: !selectedContactUuids.length }}
        onOk={() => void submitContactAssociation()}
        onCancel={() => setContactAssociationOpen(false)}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Select
            mode="multiple"
            showSearch
            allowClear
            autoFocus
            value={selectedContactUuids}
            options={contactAssociationOptions}
            loading={contactAssociationLoading}
            filterOption={false}
            placeholder="搜索并选择未归属客户的联系人"
            style={{ width: '100%' }}
            onChange={setSelectedContactUuids}
            onSearch={(keyword) => void loadContactAssociationOptions(keyword)}
            notFoundContent={
              contactAssociationLoading ? <Skeleton active /> : undefined
            }
          />
          <div>
            <Text type="secondary">没有找到合适的联系人？</Text>
            <Button
              type="link"
              onClick={() => {
                setContactAssociationOpen(false);
                setContactCreateOpen(true);
              }}
            >
              新建联系人
            </Button>
          </div>
        </Space>
      </Modal>
      <ContactEditModal
        open={contactCreateOpen || Boolean(editingContactUuid)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setContactCreateOpen(false);
            setEditingContactUuid(undefined);
          }
        }}
        recordUuid={editingContactUuid}
        currentMemberUuid={undefined}
        context={
          editingContactUuid
            ? undefined
            : {
                customerUuid: customer?.uuid,
              }
        }
        onSuccess={() => {
          setContactCreateOpen(false);
          setEditingContactUuid(undefined);
          onRefresh();
        }}
      />
      <LeadEditModal
        open={leadEditOpen}
        onOpenChange={(nextOpen) => {
          setLeadEditOpen(nextOpen);
          if (!nextOpen) {
            setEditingLeadUuid(undefined);
          }
        }}
        leadUuid={editingLeadUuid}
        defaultCustomerUuid={customer?.uuid}
        defaultCustomerName={normalizeText(customer?.name)}
        currentMemberUuid={undefined}
        leadViewMode={pageViewMode}
        onSuccess={() => {
          setLeadEditOpen(false);
          setEditingLeadUuid(undefined);
          void loadCustomerLeads();
          onRefresh();
        }}
      />
      <OpportunityEditModal
        open={Boolean(opportunityEditor)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setOpportunityEditor(null);
          }
        }}
        opportunityUuid={
          opportunityEditor?.mode === 'edit'
            ? opportunityEditor.recordUuid
            : undefined
        }
        currentMemberUuid={undefined}
        context={{
          customerUuid: customer?.uuid,
          customerName: normalizeText(customer?.name),
        }}
        onSuccess={async () => {
          setOpportunityEditor(null);
          await loadCustomerOpportunities();
          onRefresh();
        }}
      />
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={linkOpportunityOpen}
        title="关联已有商机"
        okText="确认关联"
        cancelText="取消"
        confirmLoading={linkOpportunitySubmitting}
        okButtonProps={{ disabled: !selectedLinkOpportunityUuid }}
        onOk={() => void submitLinkOpportunity()}
        onCancel={() => {
          setLinkOpportunityOpen(false);
          setSelectedLinkOpportunityUuid(undefined);
        }}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Select
            showSearch
            allowClear
            autoFocus
            value={selectedLinkOpportunityUuid}
            options={linkOpportunityOptions}
            loading={linkOpportunityLoading}
            filterOption={false}
            placeholder="搜索并选择要关联的商机"
            style={{ width: '100%' }}
            onChange={(value) => setSelectedLinkOpportunityUuid(value)}
            onSearch={(keyword) => void loadLinkOpportunityOptions(keyword)}
            notFoundContent={
              linkOpportunityLoading ? <Skeleton active /> : undefined
            }
          />
          <div>
            <Text type="secondary">没有找到合适的商机？</Text>
            <Button
              type="link"
              onClick={() => {
                setLinkOpportunityOpen(false);
                setSelectedLinkOpportunityUuid(undefined);
                setOpportunityEditor({ mode: 'create' });
              }}
            >
              新建商机
            </Button>
          </div>
        </Space>
      </Modal>
      <QuoteEditorModal
        open={Boolean(quoteEditor)}
        mode={quoteEditor?.mode || 'create'}
        quoteUuid={
          quoteEditor?.mode === 'edit' ? quoteEditor.recordUuid : undefined
        }
        context={{
          customerUuid: customer?.uuid,
          customerName: normalizeText(customer?.name),
          ownerUserUuid: normalizeText(customer?.ownerUserUuid),
        }}
        onCancel={closeQuoteEditor}
        onSuccess={() => {
          closeQuoteEditor();
          onRefresh();
        }}
      />
      <ContractEditModal
        open={Boolean(contractEditor)}
        mode={contractEditor?.mode || 'create'}
        recordUuid={
          contractEditor?.mode === 'edit'
            ? contractEditor.recordUuid
            : undefined
        }
        context={{
          customerUuid: customer?.uuid,
          customerName: normalizeText(customer?.name),
          ownerUserUuid: normalizeText(customer?.ownerUserUuid),
          sourceObjectType: 'CUSTOMER',
        }}
        onCancel={closeContractEditor}
        onSuccess={async () => {
          closeContractEditor();
          onRefresh();
        }}
      />
      <InvoiceEditModal
        open={Boolean(invoiceEditor)}
        mode={invoiceEditor?.mode || 'create'}
        recordUuid={
          invoiceEditor?.mode === 'edit' ? invoiceEditor.recordUuid : undefined
        }
        context={{
          customerUuid: customer?.uuid,
          customerName: normalizeText(customer?.name),
          ownerUserUuid: normalizeText(customer?.ownerUserUuid),
        }}
        onCancel={closeInvoiceEditor}
        onSuccess={async () => {
          closeInvoiceEditor();
          onRefresh();
        }}
      />
      <FollowUpCreateModal
        open={quickFollowUpOpen || Boolean(editingFollowUp)}
        title={editingFollowUp ? '编辑跟进记录' : '新建跟进记录'}
        onCancel={() => {
          setQuickFollowUpOpen(false);
          if (editingFollowUp) {
            setRelatedEditor(null);
          }
        }}
        confirmLoading={quickFollowUpSubmitting}
        context={{
          bizType: 2,
          bizUuid: customer?.uuid || '',
          bizName: customer?.name,
          defaultCustomerUuids: customer?.uuid ? [customer.uuid] : undefined,
        }}
        editTarget={
          editingFollowUp ? { uuid: editingFollowUp.uuid } : undefined
        }
        defaultValues={
          editingFollowUp
            ? {
                followUpType: editingFollowUp.followUpType,
                goalTag: editingFollowUp.goalTag,
                resultTag: editingFollowUp.resultTag,
                content: editingFollowUp.content,
                qualityScore: editingFollowUp.qualityScore,
                nextStepConfirmed: editingFollowUp.nextStepConfirmed,
                nextStepContent: editingFollowUp.nextStepContent,
                nextStepAt: editingFollowUp.nextStepAt
                  ? dayjs(editingFollowUp.nextStepAt)
                  : undefined,
                nextTime: editingFollowUp.nextTime
                  ? dayjs(editingFollowUp.nextTime)
                  : undefined,
                stageAdvanced: editingFollowUp.stageAdvanced,
                keySignal: editingFollowUp.keySignal,
                lostReasonHint: editingFollowUp.lostReasonHint,
                syncOpportunityAdvance: editingFollowUp.syncOpportunityAdvance,
              }
            : undefined
        }
        onSuccess={async () => {
          setQuickFollowUpOpen(false);
          if (editingFollowUp) {
            setRelatedEditor(null);
          }
          onRefresh();
        }}
      />
    </>
  );

  if (isPageMode) {
    return content;
  }

  return (
    <BusinessModal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      title={null}
      detail
      aria-label="客户详情"
    >
      {content}
    </BusinessModal>
  );
}

function resolveSections(
  profile?: CustomerDrawerProfile,
): CustomerDrawerSection[] {
  if (profile?.sections?.length) {
    const sectionMap = new Map(
      profile.sections.map((section) => [section.key, section]),
    );
    for (const section of SECTION_LABELS) {
      if (!sectionMap.has(section.key)) {
        sectionMap.set(section.key, section);
      }
    }
    return normalizeDetailSections('customer', Array.from(sectionMap.values()));
  }

  const contactCount = profile?.related?.contacts?.total || 0;
  const leadCount = profile?.related?.leads?.total || 0;
  const opportunityCount = profile?.related?.opportunities?.total || 0;
  const quoteCount = profile?.related?.quotes?.total || 0;
  const contractCount = profile?.related?.contracts?.total || 0;
  const receiptCount =
    (profile?.related?.receipts?.total || 0) +
    (profile?.related?.receiptSchedules?.total || 0);
  const invoiceCount = profile?.related?.invoices?.total || 0;
  const productCount = profile?.related?.products?.total || 0;
  const teamMemberCount = profile?.related?.teamMembers?.total || 0;
  const activityCount = profile?.activities?.length || 0;

  return normalizeDetailSections(
    'customer',
    SECTION_LABELS.map((section) => {
      switch (section.key) {
        case 'activities':
          return { ...section, count: activityCount };
        case 'contacts':
          return { ...section, count: contactCount };
        case 'leads':
          return { ...section, count: leadCount };
        case 'teamMembers':
          return { ...section, count: teamMemberCount };
        case 'opportunities':
          return { ...section, count: opportunityCount };
        case 'quotes':
          return { ...section, count: quoteCount };
        case 'contracts':
          return { ...section, count: contractCount };
        case 'products':
          return { ...section, count: productCount };
        case 'receipts':
          return { ...section, count: receiptCount };
        case 'invoices':
          return { ...section, count: invoiceCount };
        default:
          return section;
      }
    }),
  );
}

function CustomerFollowUpRecordPanel({
  customerUuid,
  customerName,
  pageViewMode,
  scopeRefreshKey,
  onRefresh,
  onTotalChange,
}: {
  onRefresh: () => void;
  customerUuid: string;
  customerName?: string;
  pageViewMode: CrmScopedViewMode;
  scopeRefreshKey: string;
  onTotalChange: (total: number) => void;
}) {
  const [relatedScopes, setRelatedScopes] = useState<
    Array<{ targetType: string; targetUuid: string }>
  >([]);

  useEffect(() => {
    let active = true;
    const loadAll = async <T,>(
      loadPage: (page: number) => Promise<{ list: T[]; total: number }>,
    ) => {
      const firstPage = await loadPage(1);
      const pageCount = Math.ceil(
        Number(firstPage.total || 0) / CUSTOMER_FOLLOW_UP_SCOPE_PAGE_SIZE,
      );
      if (pageCount <= 1) return firstPage.list;
      const remainingPages = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) =>
          loadPage(index + 2),
        ),
      );
      return [
        ...firstPage.list,
        ...remainingPages.flatMap((page) => page.list),
      ];
    };

    void Promise.all([
      loadAll((page) =>
        queryContacts({
          customerUuid,
          viewMode: pageViewMode,
          page,
          pageSize: CUSTOMER_FOLLOW_UP_SCOPE_PAGE_SIZE,
        }),
      ),
      loadAll((page) =>
        queryOpportunities({
          customerUuid,
          viewMode: pageViewMode,
          page,
          pageSize: CUSTOMER_FOLLOW_UP_SCOPE_PAGE_SIZE,
        }),
      ),
    ])
      .then(([contacts, opportunities]) => {
        if (!active) return;
        setRelatedScopes([
          ...contacts.map((item) => ({
            targetType: 'contact',
            targetUuid: item.uuid,
          })),
          ...opportunities.map((item) => ({
            targetType: 'opportunity',
            targetUuid: item.uuid,
          })),
        ]);
      })
      .catch(() => {
        if (active) setRelatedScopes([]);
      });

    return () => {
      active = false;
    };
  }, [customerUuid, pageViewMode, scopeRefreshKey]);

  return (
    <ActivityPanelFollowUps
      bizType="customer"
      bizUuid={customerUuid}
      bizName={customerName}
      relatedScopes={relatedScopes}
      onTotalChange={onTotalChange}
      onRecordSaved={onRefresh}
    />
  );
}

function renderSectionContent(
  sectionKey: CustomerDrawerSectionKey | undefined,
  profile: CustomerDrawerProfile,
  leadPage: CustomerDrawerRelatedPage<LeadItem>,
  leadLoading: boolean,
  opportunityPage: CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>,
  opportunityLoading: boolean,
  lookups: CustomerDrawerLookups,
  businessBoard: Array<{
    key: string;
    title: string;
    value: string;
    hint: string;
  }>,
  nextActions: CustomerNextAction[],
  healthSignals: CustomerHealthSignal[],
  _relationContext: RelationCreateContext,
  onEditCustomer: () => void,
  onCreateOpportunity: () => void,
  onEditOpportunity: (record: CustomerDrawerOpportunityItem) => void,
  onLinkOpportunity: () => void,
  onCreateContact: () => void,
  onEditContact: (record: CustomerDrawerContactItem) => void,
  onDeleteContact: (record: CustomerDrawerContactItem) => void,
  onCreateLead: () => void,
  onEditLead: (record: LeadItem) => void,
  onDeleteLead: (record: LeadItem) => void,
  onRefreshLead: () => void,
  _onEditRelated: (state: RelatedEditorState) => void,
  onCreateQuote: () => void,
  onEditQuote: (record: CustomerDrawerQuoteItem) => void,
  onCreateContract: () => void,
  onEditContract: (record: CustomerDrawerContractItem) => void,
  onCreateInvoice: () => void,
  onEditInvoice: (record: CustomerDrawerInvoiceItem) => void,
  _onCreateFollowUp: () => void,
  _onCreateSchedule: () => void,
  _scheduleForm: FormInstance<CalendarEventFormValues>,
  _scheduleEditor: ScheduleEditorState | null,
  _scheduleSubmitting: boolean,
  _relatedMemberOptions: RelationOption[],
  _relatedMemberLoading: boolean,
  _relatedMemberLoadFailed: boolean,
  _onSearchRelatedMembers: (keyword?: string) => void,
  _onCloseScheduleEditor: () => void,
  _onSubmitScheduleEditor: () => void,
  onCreateAddress: () => void,
  onEditAddress: (record: CustomerAddressItem) => void,
  onDeleteAddress: (record: CustomerAddressItem) => void,
  pageViewMode: CrmScopedViewMode,
  onRefresh: () => void,
  onAttachmentCountChange: (count: number) => void,
  onFollowUpCountChange: (total: number) => void,
) {
  if (!sectionKey) return <Empty description="当前无可查看的分区" />;
  const related = profile.related || ({} as CustomerDrawerProfile['related']);
  const contacts = related.contacts || EMPTY_PAGE;
  const leads = leadPage || EMPTY_PAGE;
  const opportunities = opportunityPage || EMPTY_PAGE;
  const contracts = related.contracts || EMPTY_PAGE;
  const quotes = related.quotes || EMPTY_PAGE;
  const receipts = related.receipts || EMPTY_PAGE;
  const receiptSchedules = related.receiptSchedules || EMPTY_PAGE;
  const invoices = related.invoices || EMPTY_PAGE;
  const products = related.products || EMPTY_PAGE;
  const _attachments = related.attachments || EMPTY_PAGE;
  const referralsIn = related.referralsIn || EMPTY_PAGE;
  const referralsOut = related.referralsOut || EMPTY_PAGE;

  switch (sectionKey) {
    case 'activities':
      return (
        <SectionPanel title="活动">
          <ActivityPanel
            bizType="customer"
            bizUuid={profile.customer.uuid}
            bizName={profile.customer.name}
            activities={profile.activities || []}
          />
        </SectionPanel>
      );
    case 'followUps':
      return (
        <SectionPanel title="跟进记录">
          <CustomerFollowUpRecordPanel
            onRefresh={onRefresh}
            onTotalChange={onFollowUpCountChange}
            customerUuid={profile.customer.uuid}
            customerName={profile.customer.name}
            pageViewMode={pageViewMode}
            scopeRefreshKey={`${related.contacts?.total || 0}:${related.opportunities?.total || 0}`}
          />
        </SectionPanel>
      );
    case 'tasks':
      return (
        <CrmTaskRelationPanel
          title={detailSectionLabel('tasks')}
          sourceSceneUuid="customer"
          sourceObjectUuid={profile.customer.uuid}
          className={styles.sectionCard}
        />
      );
    case 'businessTrips':
      return (
        <BusinessTripRelationPanel
          title={detailSectionLabel('businessTrips')}
          objectType="customer"
          objectUuid={profile.customer.uuid}
          className={styles.sectionCard}
        />
      );
    case 'details':
      return (
        <DetailSection
          customer={profile.customer}
          lookups={lookups}
          onEdit={onEditCustomer}
          addresses={
            profile.sections?.some(
              (section) =>
                section.key === 'customerAddress' && section.enabled === false,
            )
              ? undefined
              : related.addresses || []
          }
          onCreateAddress={onCreateAddress}
          onEditAddress={onEditAddress}
          onDeleteAddress={onDeleteAddress}
        />
      );
    case 'overview360':
      return (
        <OverviewSection
          profile={profile}
          businessBoard={businessBoard}
          nextActions={nextActions}
          healthSignals={healthSignals}
          scheduleSummary={buildReceiptScheduleSummary(receiptSchedules.list)}
          sectionTotals={{
            contacts: contacts.total,
            opportunities: opportunities.total,
            quotes: quotes.total,
            contracts: contracts.total,
            products: products.total,
            receipts: receipts.total,
            receiptSchedules: receiptSchedules.total,
            invoices: invoices.total,
          }}
        />
      );
    case 'referrals':
      return (
        <ReferralSection
          customerUuid={profile.customer.uuid}
          incoming={referralsIn}
          outgoing={referralsOut}
          onRefresh={onRefresh}
        />
      );
    case 'contacts':
      return (
        <ContactSection
          page={contacts}
          onCreate={onCreateContact}
          onEdit={onEditContact}
          onDelete={onDeleteContact}
          viewMode={pageViewMode}
        />
      );
    case 'leads':
      return (
        <LeadSection
          page={leads}
          loading={leadLoading}
          memberNameMap={lookups.memberNameMap}
          onCreate={onCreateLead}
          onEdit={onEditLead}
          onDelete={onDeleteLead}
          onRefresh={onRefreshLead}
        />
      );
    case 'teamMembers':
      return (
        <TeamMemberSection
          customerUuid={profile.customer.uuid}
          viewMode={pageViewMode}
          onRefresh={onRefresh}
        />
      );
    case 'opportunities':
      return (
        <OpportunitySection
          page={opportunities}
          loading={opportunityLoading}
          onCreate={onCreateOpportunity}
          onEdit={onEditOpportunity}
          onLink={onLinkOpportunity}
        />
      );
    case 'quotes':
      return (
        <QuoteSection
          page={quotes}
          onCreate={onCreateQuote}
          onEdit={onEditQuote}
        />
      );
    case 'contracts':
      return (
        <ContractSection
          page={contracts}
          onCreate={onCreateContract}
          onEdit={onEditContract}
          onRefresh={onRefresh}
        />
      );
    case 'products':
      return <ProductSection page={products} />;
    case 'receipts':
      return (
        <ReceiptPanel
          title={detailSectionLabel('receipts')}
          bizType="customer"
          bizUuid={profile.customer.uuid}
        />
      );
    case 'invoices':
      return (
        <InvoiceSection
          page={invoices}
          onCreate={onCreateInvoice}
          onEdit={onEditInvoice}
        />
      );
    case 'attachments':
      return (
        <AttachmentPanel
          bizType="customer"
          bizUuid={profile.customer.uuid}
          createLabel="新增附件"
          onDataLoaded={(page) => onAttachmentCountChange(page.total)}
        />
      );
    default:
      return <UnsupportedSection sectionKey={sectionKey} />;
  }
}

function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.metaItem}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SectionPanel({ title, hint, extra, children }: SectionPanelProps) {
  return (
    <ProCard
      title={title}
      subTitle={
        hint ? <span className={styles.cardSubTitle}>{hint}</span> : undefined
      }
      extra={extra}
      className={styles.sectionCard}
    >
      {children}
    </ProCard>
  );
}

function EmptyBlock({ description }: { description: string }) {
  return (
    <div className={styles.emptyBlock}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
    </div>
  );
}

function DetailAccessError({
  onClose,
  onRetry,
}: {
  onClose?: () => void;
  onRetry: () => void;
}) {
  return (
    <div className={styles.loading}>
      <Alert
        type="error"
        showIcon
        message="当前记录不可访问或已不存在"
        description="详情暂时无法打开，你可以关闭弹窗后返回列表确认记录状态。"
        action={
          <Space>
            <Button onClick={onRetry}>重试</Button>
            {onClose ? <Button onClick={onClose}>返回</Button> : null}
          </Space>
        }
      />
    </div>
  );
}

function pageHint<T>(page: CustomerDrawerRelatedPage<T>, noun: string) {
  if (!page.total) {
    return `暂无${noun}数据`;
  }
  return page.total > page.list.length
    ? `展示最近 ${page.list.length} 条，共 ${page.total} 条`
    : `共 ${page.total} 条`;
}

function renderReferralObject(
  type: ReferralItem['referrerType'] | ReferralItem['targetType'],
  uuid?: string,
  name?: string,
) {
  const label = name || uuid || '-';
  if (!uuid) {
    return <Text type="secondary">{label}</Text>;
  }
  switch (type) {
    case 'contact':
      return <ContactReferenceLink contactUuid={uuid} contactName={label} />;
    case 'customer':
      return (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => history.push(`/zy-crm/customer/${uuid}`)}
        >
          {label}
        </Button>
      );
    case 'lead':
      return <LeadReferenceLink leadUuid={uuid} leadName={label} />;
    case 'opportunity':
      return (
        <OpportunityReferenceLink
          opportunityUuid={uuid}
          opportunityName={label}
        />
      );
    default:
      return <Text>{label}</Text>;
  }
}

function ReferralSection({
  customerUuid,
  incoming,
  outgoing,
  onRefresh,
}: {
  customerUuid: string;
  incoming: CustomerDrawerRelatedPage<ReferralItem>;
  outgoing: CustomerDrawerRelatedPage<ReferralItem>;
  onRefresh: () => void;
}) {
  const [incomingForm] = Form.useForm<IncomingReferralFormValues>();
  const [outgoingForm] = Form.useForm<OutgoingReferralFormValues>();
  const [incomingSaving, setIncomingSaving] = useState(false);
  const [outgoingSubmitting, setOutgoingSubmitting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [leadOptions, setLeadOptions] = useState<RelationOption[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<
    RelationOption[]
  >([]);
  const [inlineCreate, setInlineCreate] =
    useState<ReferralInlineCreateState | null>(null);

  const currentMemberUuid = useMemo(() => getCurrentMemberUuid(), []);

  const incomingReferrals = useMemo(
    () => buildIncomingCustomerReferrals(incoming),
    [incoming],
  );

  const outgoingReferrals = useMemo(
    () => buildOutgoingReferrals(outgoing),
    [outgoing],
  );

  useEffect(() => {
    setCustomerOptions((prev) =>
      mergeOptions(
        prev,
        [
          ...incomingReferrals
            .filter((item) => item.referrerType === 'customer')
            .map((item) =>
              toReferralOption(
                normalizeText(item.referrerUuid),
                item.referrerName,
              ),
            ),
          ...outgoingReferrals
            .filter((item) => item.targetType === 'customer')
            .map((item) =>
              toReferralOption(normalizeText(item.targetUuid), item.targetName),
            ),
        ].filter(Boolean) as RelationOption[],
      ),
    );
    setContactOptions((prev) =>
      mergeOptions(
        prev,
        [
          ...incomingReferrals
            .filter((item) => item.referrerType === 'contact')
            .map((item) =>
              toReferralOption(
                normalizeText(item.referrerUuid),
                item.referrerName,
              ),
            ),
          ...outgoingReferrals
            .filter((item) => item.targetType === 'contact')
            .map((item) =>
              toReferralOption(normalizeText(item.targetUuid), item.targetName),
            ),
        ].filter(Boolean) as RelationOption[],
      ),
    );
    setLeadOptions((prev) =>
      mergeOptions(
        prev,
        outgoingReferrals
          .filter((item) => item.targetType === 'lead')
          .map((item) =>
            toReferralOption(normalizeText(item.targetUuid), item.targetName),
          )
          .filter(Boolean) as RelationOption[],
      ),
    );
    setOpportunityOptions((prev) =>
      mergeOptions(
        prev,
        outgoingReferrals
          .filter((item) => item.targetType === 'opportunity')
          .map((item) =>
            toReferralOption(normalizeText(item.targetUuid), item.targetName),
          )
          .filter(Boolean) as RelationOption[],
      ),
    );
  }, [incomingReferrals, outgoingReferrals]);

  const loadCustomerOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchCustomerOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setCustomerOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const loadContactOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchContactOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setContactOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const loadLeadOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchLeadOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setLeadOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const loadOpportunityOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchOpportunityOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setOpportunityOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const ensureCurrentCustomerOptions = useCallback(() => {
    void loadCustomerOptions(undefined, [
      ...incomingReferrals
        .filter((item) => item.referrerType === 'customer')
        .map((item) => ({
          value: normalizeText(item.referrerUuid),
          label: item.referrerName,
        })),
      ...outgoingReferrals
        .filter((item) => item.targetType === 'customer')
        .map((item) => ({
          value: normalizeText(item.targetUuid),
          label: item.targetName,
        })),
    ]);
  }, [incomingReferrals, loadCustomerOptions, outgoingReferrals]);

  const ensureCurrentContactOptions = useCallback(
    (record?: ReferralItem) => {
      void loadContactOptions(undefined, [
        ...incomingReferrals
          .filter((item) => item.referrerType === 'contact')
          .map((item) => ({
            value: normalizeText(item.referrerUuid),
            label: item.referrerName,
          })),
        ...(record?.referrerType === 'contact'
          ? [
              {
                value: normalizeText(record.referrerUuid),
                label: record.referrerName,
              },
            ]
          : []),
      ]);
    },
    [incomingReferrals, loadContactOptions],
  );

  const submitIncomingEdit = useCallback(
    async (
      values: IncomingReferralFormValues,
      mode: 'create' | 'edit',
      record?: ReferralItem,
    ) => {
      const incomingReferrerUuid = normalizeText(values.incomingReferrerUuid);

      validateCustomerReferralValues({
        customerUuid,
        incomingReferrerUuid,
        outgoingTargetType: undefined,
        outgoingTargetUuid: undefined,
      });

      if (values.incomingIsPrimary) {
        const primaryExists = incomingReferrals.some(
          (item) =>
            Boolean(item.isPrimary) &&
            normalizeText(item.uuid) !== normalizeText(record?.uuid),
        );
        if (primaryExists) {
          message.error('同一客户只能设置一个主介绍人');
          return;
        }
      }

      setIncomingSaving(true);
      try {
        const payload = {
          referrerType:
            values.incomingReferrerType as ReferralItem['referrerType'],
          referrerUuid: incomingReferrerUuid,
          targetType: 'customer' as const,
          targetUuid: customerUuid,
          referralTime: toIsoValue(values.incomingReferralTime),
          isPrimary: values.incomingIsPrimary ? 1 : 0,
          profitShareRatio: values.incomingProfitShareRatio,
          remark: normalizeOptionalText(values.incomingRemark),
        };
        if (mode === 'edit' && record?.uuid) {
          await updateReferral(record.uuid, payload);
        } else {
          await createReferral(payload);
        }
        message.success('介绍关系更新成功');
        onRefresh();
      } catch (error) {
        const nextError =
          error instanceof Error ? error : new Error('介绍关系保存失败');
        message.error(nextError.message);
      } finally {
        setIncomingSaving(false);
      }
    },
    [customerUuid, incomingReferrals, onRefresh],
  );

  const removeIncomingReferral = useCallback(
    (record: ReferralItem) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除介绍关系',
        content: `确认删除与${record.referrerName || record.referrerUuid}相关的介绍关系吗`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          if (!record.uuid) {
            return;
          }
          await deleteReferral(record.uuid);
          message.success('介绍关系删除成功');
          onRefresh();
        },
      });
    },
    [onRefresh],
  );

  const submitOutgoingModal = useCallback(
    async (
      values: OutgoingReferralFormValues,
      mode: 'create' | 'edit',
      record?: ReferralItem,
    ) => {
      const targetType = values.targetType as ReferralItem['targetType'];
      const targetUuid = normalizeText(values.targetUuid);

      validateCustomerReferralValues({
        customerUuid,
        incomingReferrerUuid: undefined,
        outgoingTargetType: targetType,
        outgoingTargetUuid: targetUuid,
      });

      if (
        isDuplicateOutgoingReferral({
          currentRecordUuid: record?.uuid,
          targetType,
          targetUuid,
          items: outgoingReferrals,
        })
      ) {
        message.error('当前客户介绍出去的对象不能重复');
        return;
      }

      setOutgoingSubmitting(true);
      try {
        const payload = {
          referrerType: 'customer' as const,
          referrerUuid: customerUuid,
          targetType,
          targetUuid,
          referralTime: toIsoValue(values.referralTime),
          remark: normalizeOptionalText(values.remark),
        };
        if (mode === 'edit' && record?.uuid) {
          await updateReferral(record.uuid, payload);
        } else {
          await createReferral(payload);
        }
        message.success('介绍关系更新成功');
        onRefresh();
      } catch (error) {
        const nextError =
          error instanceof Error ? error : new Error('介绍关系保存失败');
        message.error(nextError.message);
      } finally {
        setOutgoingSubmitting(false);
      }
    },
    [customerUuid, onRefresh, outgoingReferrals],
  );

  const removeOutgoingReferral = useCallback(
    (record: ReferralItem) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除介绍关系',
        content: `确认删除与${record.targetName || record.targetUuid}的介绍关系吗`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          if (!record.uuid) {
            return;
          }
          await deleteReferral(record.uuid);
          message.success('介绍关系删除成功');
          onRefresh();
        },
      });
    },
    [onRefresh],
  );

  const loadOutgoingTargetOptions = useCallback(
    async (
      targetType?: ReferralItem['targetType'],
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      if (targetType === 'customer') {
        await loadCustomerOptions(keyword, ensureValues);
      } else if (targetType === 'contact') {
        await loadContactOptions(keyword, ensureValues);
      } else if (targetType === 'lead') {
        await loadLeadOptions(keyword, ensureValues);
      } else if (targetType === 'opportunity') {
        await loadOpportunityOptions(keyword, ensureValues);
      }
    },
    [
      loadContactOptions,
      loadCustomerOptions,
      loadLeadOptions,
      loadOpportunityOptions,
    ],
  );

  const openInlineCreate = useCallback(
    (
      entity: ReferralInlineCreateState['entity'],
      form: ReferralInlineCreateState['form'],
    ) => {
      setInlineCreate({ entity, form });
    },
    [],
  );

  const handleInlineCustomerCreated = useCallback(
    (record?: CustomerItem) => {
      const customerUuid = normalizeText(record?.uuid);
      const customerName = normalizeText(record?.name) || customerUuid;
      setInlineCreate(null);

      if (!customerUuid) {
        message.warning('客户已创建，请在客户字段中搜索选择');
        void loadCustomerOptions();
        return;
      }

      const option = {
        value: customerUuid,
        label: customerName || customerUuid,
      };
      setCustomerOptions((prev) => mergeOptions(prev, [option], customerUuid));
      if (inlineCreate?.form === 'outgoing') {
        outgoingForm.setFieldsValue({
          targetType: 'customer',
          targetUuid: customerUuid,
        });
      } else {
        incomingForm.setFieldsValue({
          incomingReferrerType: 'customer',
          incomingReferrerUuid: customerUuid,
        });
      }
      void loadCustomerOptions(customerName || undefined, [option]);
    },
    [incomingForm, inlineCreate?.form, loadCustomerOptions, outgoingForm],
  );

  const handleInlineContactCreated = useCallback(
    async (record?: ContactItem) => {
      const contactUuid = normalizeText(record?.uuid);
      const contactName =
        normalizeText(record?.name) ||
        normalizeText(record?.mobile) ||
        contactUuid;
      setInlineCreate(null);

      if (!contactUuid) {
        message.warning('联系人已创建，请在联系人字段中搜索选择');
        void loadContactOptions();
        return;
      }

      const option = {
        value: contactUuid,
        label: contactName || contactUuid,
      };
      setContactOptions((prev) => mergeOptions(prev, [option], contactUuid));
      if (inlineCreate?.form === 'outgoing') {
        outgoingForm.setFieldsValue({
          targetType: 'contact',
          targetUuid: contactUuid,
        });
      } else {
        incomingForm.setFieldsValue({
          incomingReferrerType: 'contact',
          incomingReferrerUuid: contactUuid,
        });
      }
      await loadContactOptions(contactName || undefined, [option]);
    },
    [incomingForm, inlineCreate?.form, loadContactOptions, outgoingForm],
  );

  return (
    <>
      <UnifiedReferralSection
        title={detailSectionLabel('referrals')}
        hint={`传入 ${incoming.total} 条，传出 ${outgoing.total} 条`}
        renderSection={({ title, hint, children }) => (
          <SectionPanel title={title} hint={hint}>
            {children}
          </SectionPanel>
        )}
        blocks={[
          {
            key: 'incoming',
            title: '谁介绍了当前客户',
            createButtonText: '新增',
            items: incomingReferrals,
            emptyText: '',
            columns: [
              {
                title: '介绍人类型',
                dataIndex: 'referrerType',
                key: 'referrerType',
                render: (value: ReferralItem['referrerType']) => (
                  <Tag color="blue">
                    {value === 'contact' ? '联系人' : '客户'}
                  </Tag>
                ),
              },
              {
                title: '介绍人',
                dataIndex: 'referrerName',
                key: 'referrerName',
                render: (_, record: ReferralItem) =>
                  renderReferralObject(
                    record.referrerType,
                    record.referrerUuid,
                    record.referrerName,
                  ),
              },
              {
                title: '介绍时间',
                dataIndex: 'referralTime',
                key: 'referralTime',
                render: (value?: string) => formatCrmDate(value),
              },
              {
                title: '是否主介绍人',
                dataIndex: 'isPrimary',
                key: 'isPrimary',
                render: (value?: number | boolean) => (
                  <Tag color={value ? 'gold' : 'default'}>
                    {value ? '是' : '否'}
                  </Tag>
                ),
              },
              {
                title: '分润比例',
                dataIndex: 'profitShareRatio',
                key: 'profitShareRatio',
                render: (value?: number | string) =>
                  value === undefined || value === null || value === ''
                    ? '-'
                    : `${value}%`,
              },
              {
                title: '备注',
                dataIndex: 'remark',
                key: 'remark',
                render: (value?: string) => value || '-',
              },
            ],
            editor: {
              form: incomingForm,
              createTitle: '新增介绍来源',
              editTitle: '编辑介绍来源',
              submitting: incomingSaving,
              buildInitialValues: (record?: ReferralItem) =>
                buildIncomingReferralFormValues(record),
              onOpen: (record) => {
                ensureCurrentCustomerOptions();
                ensureCurrentContactOptions(record);
              },
              onSubmit: async ({ values, mode, record }) => {
                await submitIncomingEdit(values, mode, record);
              },
              fields: [
                {
                  type: 'radio',
                  name: 'incomingReferrerType',
                  label: '介绍人类型',
                  rules: [{ required: true, message: '请选择介绍人类型' }],
                  options: [
                    { label: '客户', value: 'customer' },
                    { label: '联系人', value: 'contact' },
                  ],
                  resetNames: ['incomingReferrerUuid'],
                },
                {
                  type: 'select',
                  name: 'incomingReferrerUuid',
                  label: '谁介绍了当前客户',
                  rules: [{ required: true, message: '请选择介绍人' }],
                  options: ({ form }) =>
                    form.getFieldValue('incomingReferrerType') === 'contact'
                      ? contactOptions
                      : customerOptions,
                  placeholder: ({ form }) =>
                    form.getFieldValue('incomingReferrerType') === 'contact'
                      ? '搜索并选择一个已有联系人'
                      : '搜索并选择一个已有客户',
                  disabled: ({ form }) =>
                    !form.getFieldValue('incomingReferrerType'),
                  emptyAction: ({ form }) => {
                    const type = form.getFieldValue('incomingReferrerType') as
                      | ReferralItem['referrerType']
                      | undefined;
                    if (type !== 'customer' && type !== 'contact') {
                      return null;
                    }
                    return (
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        style={{ padding: 0 }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openInlineCreate(type, 'incoming')}
                      >
                        {type === 'contact' ? '新建联系人' : '新建客户'}
                      </Button>
                    );
                  },
                  onLoadOptions: (keyword, { form, record }) => {
                    const type = form.getFieldValue('incomingReferrerType') as
                      | ReferralItem['referrerType']
                      | undefined;
                    const ensureValues =
                      type === 'contact' && record?.referrerType === 'contact'
                        ? [
                            {
                              value: normalizeText(record.referrerUuid),
                              label: record.referrerName,
                            },
                          ]
                        : type === 'customer' &&
                            record?.referrerType === 'customer'
                          ? [
                              {
                                value: normalizeText(record.referrerUuid),
                                label: record.referrerName,
                              },
                            ]
                          : [];
                    if (type === 'contact') {
                      void loadContactOptions(keyword, ensureValues);
                    } else if (type === 'customer') {
                      void loadCustomerOptions(keyword, ensureValues);
                    }
                  },
                },
                {
                  type: 'date',
                  name: 'incomingReferralTime',
                  label: '介绍时间',
                  showTime: true,
                  placeholder: '请选择介绍时间',
                },
                {
                  type: 'switch',
                  name: 'incomingIsPrimary',
                  label: '是否主介绍人',
                  checkedChildren: '是',
                  unCheckedChildren: '否',
                },
                {
                  type: 'number',
                  name: 'incomingProfitShareRatio',
                  label: '分润比例',
                  min: 0,
                  max: 100,
                  precision: 2,
                  addonAfter: '%',
                  placeholder: '请输入分润比例',
                },
                {
                  type: 'textarea',
                  name: 'incomingRemark',
                  label: '备注',
                  rows: 3,
                  maxLength: 500,
                  showCount: true,
                  placeholder: '请输入备注',
                },
              ],
            },
            onDelete: removeIncomingReferral,
          },
          {
            key: 'outgoing',
            title: '当前客户介绍出去的对象',
            createButtonText: '新增',
            items: outgoingReferrals,
            emptyText: '',
            columns: [
              {
                title: '目标类型',
                dataIndex: 'targetType',
                key: 'targetType',
                render: (value: ReferralItem['targetType']) => (
                  <Tag color="blue">
                    {value === 'contact'
                      ? '联系人'
                      : value === 'customer'
                        ? '客户'
                        : value === 'lead'
                          ? '线索'
                          : value === 'opportunity'
                            ? '商机'
                            : value}
                  </Tag>
                ),
              },
              {
                title: '介绍出去的对象',
                dataIndex: 'targetName',
                key: 'targetName',
                render: (_, record: ReferralItem) =>
                  renderReferralObject(
                    record.targetType,
                    record.targetUuid,
                    record.targetName,
                  ),
              },
              {
                title: '介绍时间',
                dataIndex: 'referralTime',
                key: 'referralTime',
                render: (value?: string) => formatCrmDate(value),
              },
              {
                title: '备注',
                dataIndex: 'remark',
                key: 'remark',
                render: (value?: string) => value || '-',
              },
            ],
            editor: {
              form: outgoingForm,
              createTitle: '新增介绍对象',
              editTitle: '编辑介绍对象',
              submitting: outgoingSubmitting,
              buildInitialValues: (record?: ReferralItem) => ({
                targetType: record?.targetType,
                targetUuid: normalizeText(record?.targetUuid) || undefined,
                referralTime: record?.referralTime
                  ? dayjs(record.referralTime)
                  : undefined,
                remark: normalizeOptionalText(record?.remark),
              }),
              onOpen: (record) => {
                ensureCurrentCustomerOptions();
                if (record?.targetType === 'contact') {
                  void loadContactOptions(undefined, [
                    {
                      value: normalizeText(record.targetUuid),
                      label: record.targetName,
                    },
                  ]);
                } else if (record?.targetType === 'lead') {
                  void loadLeadOptions(undefined, [
                    {
                      value: normalizeText(record.targetUuid),
                      label: record.targetName,
                    },
                  ]);
                } else if (record?.targetType === 'opportunity') {
                  void loadOpportunityOptions(undefined, [
                    {
                      value: normalizeText(record.targetUuid),
                      label: record.targetName,
                    },
                  ]);
                }
              },
              onSubmit: async ({ values, mode, record }) => {
                await submitOutgoingModal(values, mode, record);
              },
              fields: [
                {
                  type: 'radio',
                  name: 'targetType',
                  label: '目标类型',
                  rules: [{ required: true, message: '请选择目标类型' }],
                  options: [
                    { label: '联系人', value: 'contact' },
                    { label: '客户', value: 'customer' },
                    { label: '线索', value: 'lead' },
                  ],
                  resetNames: ['targetUuid'],
                },
                {
                  type: 'select',
                  name: 'targetUuid',
                  label: '介绍出去的对象',
                  rules: [{ required: true, message: '请选择目标对象' }],
                  options: ({ form }) => {
                    const targetType = form.getFieldValue('targetType') as
                      | ReferralItem['targetType']
                      | undefined;
                    if (targetType === 'customer') return customerOptions;
                    if (targetType === 'contact') return contactOptions;
                    if (targetType === 'lead') return leadOptions;
                    if (targetType === 'opportunity') return opportunityOptions;
                    return [];
                  },
                  placeholder: ({ form }) => {
                    const targetType = form.getFieldValue('targetType') as
                      | ReferralItem['targetType']
                      | undefined;
                    if (targetType === 'contact') {
                      return '搜索并选择一个已有联系人';
                    }
                    if (targetType === 'customer') {
                      return '搜索并选择一个已有客户';
                    }
                    if (targetType === 'lead') {
                      return '搜索并选择一个已有线索';
                    }
                    if (targetType === 'opportunity') {
                      return '搜索并选择一个已有商机';
                    }
                    return '请先选择目标类型';
                  },
                  disabled: ({ form }) => !form.getFieldValue('targetType'),
                  emptyAction: ({ form }) => {
                    const targetType = form.getFieldValue('targetType') as
                      | ReferralItem['targetType']
                      | undefined;
                    if (targetType !== 'customer' && targetType !== 'contact') {
                      return null;
                    }
                    return (
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        style={{ padding: 0 }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openInlineCreate(targetType, 'outgoing')}
                      >
                        {targetType === 'contact' ? '新建联系人' : '新建客户'}
                      </Button>
                    );
                  },
                  onLoadOptions: (keyword, { form, record }) => {
                    const targetType = form.getFieldValue('targetType') as
                      | ReferralItem['targetType']
                      | undefined;
                    const ensureValues =
                      record && record.targetType === targetType
                        ? [
                            {
                              value: normalizeText(record.targetUuid),
                              label: record.targetName,
                            },
                          ]
                        : [];
                    void loadOutgoingTargetOptions(
                      targetType,
                      keyword,
                      ensureValues,
                    );
                  },
                },
                {
                  type: 'date',
                  name: 'referralTime',
                  label: '介绍时间',
                  showTime: true,
                  placeholder: '请选择介绍时间',
                },
                {
                  type: 'textarea',
                  name: 'remark',
                  label: '备注',
                  rows: 3,
                  maxLength: 500,
                  showCount: true,
                  placeholder: '请输入备注',
                },
              ],
            },
            onDelete: removeOutgoingReferral,
          },
        ]}
      />
      <CustomerEditModal
        open={inlineCreate?.entity === 'customer'}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setInlineCreate(null);
          }
        }}
        currentMemberUuid={currentMemberUuid}
        zIndex={1200}
        onSuccess={handleInlineCustomerCreated}
      />
      <ContactEditModal
        open={inlineCreate?.entity === 'contact'}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setInlineCreate(null);
          }
        }}
        currentMemberUuid={currentMemberUuid}
        zIndex={1200}
        onSuccess={handleInlineContactCreated}
      />
    </>
  );
}

function DetailSection({
  customer,
  lookups,
  onEdit,
  addresses,
  onCreateAddress,
  onEditAddress,
  onDeleteAddress,
}: {
  customer: CustomerItem;
  lookups: CustomerDrawerLookups;
  onEdit: () => void;
  addresses?: CustomerAddressItem[];
  onCreateAddress: () => void;
  onEditAddress: (record: CustomerAddressItem) => void;
  onDeleteAddress: (record: CustomerAddressItem) => void;
}) {
  return (
    <div className={styles.panelStack} data-crm-stack>
      <SectionPanel
        title={detailSectionLabel('details')}
        extra={
          <Button type="link" icon={<EditOutlined />} onClick={onEdit}>
            编辑
          </Button>
        }
      >
        <ProDescriptions column={descriptionColumns} size="small">
          <ProDescriptions.Item label="客户名称">
            {customer.name || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="标签" span={2}>
            <EntityTagEditor
              entityType="customer"
              entityUuid={customer.uuid}
              tags={customer.tags}
              editable={customer.recordPermission?.actions?.edit !== false}
            />
          </ProDescriptions.Item>
          <ProDescriptions.Item label="官网">
            {customer.website || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="负责人">
            {ownerText(customer, lookups.memberNameMap)}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="锁定状态">
            {boolTag(customer.lockStatus)}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="成交状态">
            {boolTag(customer.dealStatus)}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="最近联系">
            {formatCrmDate(customer.contactLastTime, 'date')}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="下次联系">
            {formatCrmDate(customer.contactNextTime, 'date')}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="归属时间">
            {formatCrmDate(customer.ownerTime)}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="跟进状态">
            {customer.followUpStatus}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="最近联系内容" span={2}>
            {customer.contactLastContent || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="备注" span={2}>
            {customer.remark || '-'}
          </ProDescriptions.Item>
        </ProDescriptions>
      </SectionPanel>

      <CompanyInfoSection customer={customer} />

      <SectionPanel title="联系方式">
        <ProDescriptions column={descriptionColumns} size="small">
          <ProDescriptions.Item label="手机号">
            {customer.mobile || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="电话">
            {customer.telephone || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="邮箱">
            {customer.email || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="微信">
            {customer.wechat || '-'}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="QQ">
            {customer.qq || '-'}
          </ProDescriptions.Item>
        </ProDescriptions>
      </SectionPanel>

      {addresses && (
        <AddressSection
          addresses={addresses}
          onCreate={onCreateAddress}
          onEdit={onEditAddress}
          onDelete={onDeleteAddress}
        />
      )}

      <SectionPanel title="业务信息">
        <ProDescriptions column={descriptionColumns} size="small">
          <ProDescriptions.Item label="同花顺行业">
            {resolveRelationLabel(
              customer.industryUuid,
              lookups.industryOptions,
              cleanTechnicalText(customer.industryName, customer.industryUuid),
            )}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="证监会行业">
            {resolveRelationLabel(
              customer.csrcIndustryUuid,
              lookups.csrcIndustryOptions,
              cleanTechnicalText(
                customer.csrcIndustryName,
                customer.csrcIndustryUuid,
              ),
            )}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="客户等级">
            {resolveRelationLabel(
              String(customer.customerLevel || ''),
              lookups.customerLevelOptions,
            )}
          </ProDescriptions.Item>
          <ProDescriptions.Item label="来源渠道">
            {resolveRelationLabel(
              String(customer.sourceChannel || ''),
              lookups.sourceChannelOptions,
            )}
          </ProDescriptions.Item>
        </ProDescriptions>
      </SectionPanel>
    </div>
  );
}

function CompanyInfoSection({ customer }: { customer: CustomerItem }) {
  return (
    <SectionPanel title="工商信息">
      <ProDescriptions column={descriptionColumns} size="small">
        <ProDescriptions.Item label="客户全称" span={2}>
          {customer.fullName || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="统一社会信用代码">
          {customer.unifiedSocialCreditCode || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="法定代表人">
          {customer.legalRepresentative || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="注册资本">
          {customer.registeredCapital || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="成立时间">
          {formatCrmDate(customer.establishmentDate, 'date')}
        </ProDescriptions.Item>
        {(
          [
            ['qichachaUrl', '企查查网址'],
            ['tianyanchaUrl', '天眼查网址'],
          ] as const
        ).map(([field, label]) => (
          <ProDescriptions.Item key={field} label={label} span={2}>
            {customer[field] ? (
              <Typography.Link
                href={customer[field]}
                style={{ overflowWrap: 'anywhere' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {customer[field]}
              </Typography.Link>
            ) : (
              '-'
            )}
          </ProDescriptions.Item>
        ))}
      </ProDescriptions>
    </SectionPanel>
  );
}

function OverviewSection({
  profile,
  businessBoard,
  nextActions,
  healthSignals,
  sectionTotals,
  scheduleSummary,
}: {
  profile: CustomerDrawerProfile;
  businessBoard: Array<{
    key: string;
    title: string;
    value: string;
    hint: string;
  }>;
  nextActions: CustomerNextAction[];
  healthSignals: CustomerHealthSignal[];
  sectionTotals: Record<string, number>;
  scheduleSummary: {
    receivedTotal: number;
    occupyingTotal: number;
    availableTotal: number;
  };
}) {
  const summary = profile.summary;
  const related = profile.related;
  const relationship = profile.relationship;
  const relationshipMetrics = [
    { label: '跟进次数', value: summary.followUpCount },
    {
      label: '未跟进时长',
      value:
        summary.unfollowedDays === null ? '-' : `${summary.unfollowedDays} 天`,
    },
    { label: '联系人数', value: sectionTotals.contacts },
    { label: '协作成员', value: related?.teamMembers?.total ?? 0 },
    { label: '商机数', value: sectionTotals.opportunities },
    { label: '报价单', value: sectionTotals.quotes },
    { label: '合同数', value: sectionTotals.contracts },
    { label: '产品行', value: sectionTotals.products },
  ];
  const amountMetrics = [
    {
      label: '商机总额',
      value: `${formatMoney(summary.opportunityTotalAmount)} 元`,
    },
    {
      label: '合同总额',
      value: `${formatMoney(summary.contractTotalAmount)} 元`,
    },
    {
      label: '回款总额',
      value: `${formatMoney(summary.receiptTotalAmount)} 元`,
    },
    {
      label: '已回款金额',
      value: `${formatMoney(scheduleSummary.receivedTotal)} 元`,
    },
    {
      label: '占用金额',
      value: `${formatMoney(scheduleSummary.occupyingTotal)} 元`,
    },
    {
      label: '可回款余额',
      value: `${formatMoney(scheduleSummary.availableTotal)} 元`,
    },
    {
      label: '逾期回款',
      value: `${formatMoney(summary.overdueScheduleAmount)} 元`,
    },
    {
      label: '发票总额',
      value: `${formatMoney(summary.invoiceTotalAmount)} 元`,
    },
    {
      label: '最近回款',
      value: summary.latestReceiptDate
        ? formatCrmDate(summary.latestReceiptDate)
        : '-',
    },
    {
      label: '回款健康分',
      value: summary.healthScore,
    },
    { label: '待处理事项', value: summary.pendingCount },
  ];
  const relationshipSummary = relationship?.healthSummary;

  return (
    <div className={styles.panelStack} data-crm-stack>
      <SectionPanel
        title={detailSectionLabel('overview360')}
        hint="聚合风险动作和经营摘要"
      >
        <div className={styles.overviewFocusList}>
          {nextActions.map((item) => (
            <div key={item.key} className={styles.overviewFocusItem}>
              <div className={styles.overviewFocusTitle}>{item.title}</div>
              <div className={styles.overviewFocusDesc}>{item.description}</div>
            </div>
          ))}
        </div>
        <div className={styles.overviewSignalList}>
          {healthSignals.slice(0, 4).map((item) => (
            <div key={item.key} className={styles.overviewSignalItem}>
              <span className={styles.overviewSignalLabel}>{item.label}</span>
              <span className={styles.overviewSignalValue}>{item.value}</span>
            </div>
          ))}
        </div>
        <div className={styles.overviewMetricList}>
          {businessBoard.map((item) => (
            <div key={item.key} className={styles.overviewMetricItem}>
              <div className={styles.overviewMetricMain}>
                <span className={styles.overviewMetricTitle}>{item.title}</span>
                <span className={styles.overviewMetricValue}>{item.value}</span>
              </div>
              <div className={styles.overviewMetricHint}>{item.hint}</div>
            </div>
          ))}
        </div>
      </SectionPanel>

      <SectionPanel title="经营指标" hint="保留全量指标，方便横向浏览">
        <div className={styles.overviewStack}>
          <div>
            <div className={styles.groupTitle}>客户经营概览</div>
            <ProCard ghost wrap gutter={[12, 12]}>
              {relationshipMetrics.map((item) => (
                <ProCard
                  key={item.label}
                  colSpan={{ xs: 24, sm: 12, xl: 8, xxl: 6 }}
                  className={styles.metricCard}
                >
                  <Statistic title={item.label} value={item.value} />
                </ProCard>
              ))}
            </ProCard>
          </div>

          <div>
            <div className={styles.groupTitle}>金额与单据概览</div>
            <ProCard ghost wrap gutter={[12, 12]}>
              {amountMetrics.map((item) => (
                <ProCard
                  key={item.label}
                  colSpan={{ xs: 24, sm: 12, xl: 8, xxl: 6 }}
                  className={styles.metricCard}
                >
                  <Statistic title={item.label} value={item.value} />
                </ProCard>
              ))}
            </ProCard>
          </div>

          <div>
            <div className={styles.groupTitle}>关系健康摘要</div>
            <ProCard ghost wrap gutter={[12, 12]}>
              {[
                {
                  label: '关键联系人数',
                  value: relationshipSummary?.keyContactCount ?? 0,
                },
                {
                  label: '角色覆盖数',
                  value: relationshipSummary?.roleCoverageCount ?? 0,
                },
                {
                  label: '7天内已触达关键人',
                  value: relationshipSummary?.activeKeyContactCount ?? 0,
                },
                {
                  label: '30天未触达关键人',
                  value: relationshipSummary?.inactiveKeyContactCount ?? 0,
                },
                {
                  label: '决策链状态',
                  value: relationshipSummary?.decisionChainStatus || '待判断',
                },
              ].map((item) => (
                <ProCard
                  key={item.label}
                  colSpan={{ xs: 24, sm: 12, xl: 8, xxl: 6 }}
                  className={styles.metricCard}
                >
                  <Statistic title={item.label} value={item.value} />
                </ProCard>
              ))}
            </ProCard>
          </div>
        </div>
      </SectionPanel>

      <SectionPanel title="关系与明细覆盖" hint="第三层承接深度浏览和编辑">
        <div className={styles.previewMetricGrid}>
          {[
            {
              key: 'contacts',
              title: '联系人',
              value: `${sectionTotals.contacts} 位`,
              hint: '查看关键决策人和联系节奏',
            },
            {
              key: 'activities',
              title: '活动时间线',
              value: `${profile.activities.length} 条`,
              hint: '回看最近客户动态',
            },
            {
              key: 'team-members',
              title: '协作成员',
              value: `${related.teamMembers.total} 位`,
              hint: '看谁在共同推进这个客户',
            },
            {
              key: 'products',
              title: '产品明细',
              value: `${sectionTotals.products} 行`,
              hint: '查看报价、合同和商机关联产品',
            },
          ].map((item) => (
            <div key={item.key} className={styles.previewMetricCard}>
              <div className={styles.previewMetricTitle}>{item.title}</div>
              <div className={styles.previewMetricValue}>{item.value}</div>
              <div className={styles.previewMetricHint}>{item.hint}</div>
            </div>
          ))}
        </div>
      </SectionPanel>
      {!profile.sections?.some(
        (section) =>
          section.key === 'customerRelation' && section.enabled === false,
      ) && <CustomerRelationSection relationship={profile.relationship} />}
      <SectionPanel
        title="近期待办"
        hint={
          profile.pendingItems?.length
            ? `近期 ${profile.pendingItems.length} 项待处理`
            : '—'
        }
      >
        <PendingList items={profile.pendingItems || []} />
      </SectionPanel>
    </div>
  );
}

function ContactSection({
  page,
  onCreate,
  onEdit,
  onDelete,
  viewMode,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerContactItem>;
  onCreate: () => void;
  onEdit: (record: CustomerDrawerContactItem) => void;
  onDelete: (record: CustomerDrawerContactItem) => void;
  viewMode: CrmScopedViewMode;
}) {
  const access = useAccess() as Record<string, boolean>;
  const resourceCode = `contacts-${viewMode === 'pool' ? 'my' : viewMode}`;
  const canDelete = hasActionAccess(access, resourceCode, 'delete');
  return (
    <SectionPanel
      title={detailSectionLabel('contacts')}
      hint={pageHint(page, '联系人')}
      extra={
        <Button type="link" size="small" onClick={onCreate}>
          关联联系人
        </Button>
      }
    >
      {
        <Table<CustomerDrawerContactItem>
          rowKey="uuid"
          size="small"
          pagination={false}
          dataSource={page.list}
          columns={[
            {
              title: '联系人',
              key: 'name',
              render: (_, record) =>
                record.name || record.mobile || record.email || '未命名联系人',
            },
            {
              title: '职位',
              dataIndex: 'post',
              key: 'post',
              render: (value?: string) => value || '-',
            },
            {
              title: '联系方式',
              key: 'contact',
              render: (_, record) => {
                const parts = [
                  record.mobile ? `手机 ${record.mobile}` : '',
                  record.telephone ? `电话 ${record.telephone}` : '',
                  record.email ? `邮箱 ${record.email}` : '',
                ].filter(Boolean);
                return parts.length ? parts.join(' / ') : '-';
              },
            },
            {
              title: '部门',
              dataIndex: 'department',
              key: 'department',
              render: (value?: string) =>
                value ? <Tag color="purple">{value}</Tag> : '-',
            },
            {
              title: '角色标签',
              key: 'roles',
              render: (_, record) => {
                const tags: ReactNode[] = [];
                if (record.isPrimaryDecisionMaker) {
                  tags.push(
                    <Tag key="primary" color="green">
                      关键决策人
                    </Tag>,
                  );
                }
                (record.roles || []).forEach((role) => {
                  tags.push(
                    <Tag key={role} color="blue">
                      {relationshipRoleLabelMap[role] || role}
                    </Tag>,
                  );
                });
                return tags.length ? (
                  <Space wrap size={[4, 4]}>
                    {tags}
                  </Space>
                ) : (
                  '-'
                );
              },
            },
            {
              title: '关系状态',
              key: 'relationStatus',
              render: (_, record) => {
                const tags: ReactNode[] = [];
                if (record.relationshipTemperature) {
                  tags.push(
                    <Tag
                      key="temperature"
                      color={
                        relationshipTemperatureMap[
                          record.relationshipTemperature
                        ]?.color || 'default'
                      }
                    >
                      {relationshipTemperatureMap[
                        record.relationshipTemperature
                      ]?.label || record.relationshipTemperature}
                    </Tag>,
                  );
                }
                if (record.activityLevel) {
                  tags.push(
                    <Tag
                      key="activity"
                      color={
                        activityLevelMap[record.activityLevel]?.color ||
                        'default'
                      }
                    >
                      {activityLevelMap[record.activityLevel]?.label ||
                        record.activityLevel}
                    </Tag>,
                  );
                }
                return tags.length ? (
                  <Space wrap size={[4, 4]}>
                    {tags}
                  </Space>
                ) : (
                  '-'
                );
              },
            },
            {
              title: '最近联系',
              dataIndex: 'contactLastTime',
              key: 'contactLastTime',
              render: (value?: string) => formatCrmDate(value, 'date'),
            },
            {
              title: '下次联系',
              dataIndex: 'contactNextTime',
              key: 'contactNextTime',
              render: (value?: string) => formatCrmDate(value, 'date'),
            },
            {
              title: '直属上级',
              dataIndex: 'parentName',
              key: 'parentName',
              render: (value?: string) => value || '-',
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, record) => (
                <Space>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onEdit(record)}
                  >
                    编辑
                  </Button>
                  {canDelete &&
                    canReadRecord(record) &&
                    canActOnRecord(record, 'delete') && (
                      <Button
                        type="link"
                        size="small"
                        danger
                        onClick={() => onDelete(record)}
                      >
                        删除
                      </Button>
                    )}
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function renderOrgNode(
  node: NonNullable<CustomerDrawerRelationship['orgNodes']>[number],
) {
  return (
    <li key={node.uuid} className={styles.orgTreeItem}>
      <div className={styles.orgTreeCard}>
        <div className={styles.orgTreeTitle}>
          <span>{node.name || '未命名联系人'}</span>
          {node.post ? <Text type="secondary">{node.post}</Text> : null}
        </div>
        <Space wrap size={[6, 6]}>
          {node.department ? <Tag>{node.department}</Tag> : null}
          {(node.roles || []).map((role) => (
            <Tag key={role} color="blue">
              {relationshipRoleLabelMap[role] || role}
            </Tag>
          ))}
          {node.isPrimaryDecisionMaker ? (
            <Tag color="green">关键决策人</Tag>
          ) : null}
          {node.relationshipTemperature ? (
            <Tag
              color={
                relationshipTemperatureMap[node.relationshipTemperature]
                  ?.color || 'default'
              }
            >
              {relationshipTemperatureMap[node.relationshipTemperature]
                ?.label || node.relationshipTemperature}
            </Tag>
          ) : null}
        </Space>
        <div className={styles.orgTreeMeta}>
          最近联系：{formatCrmDate(node.contactLastTime, 'date')}
        </div>
      </div>
      {node.children?.length ? (
        <ul className={styles.orgTreeChildren}>
          {node.children.map((child) => renderOrgNode(child))}
        </ul>
      ) : null}
    </li>
  );
}

function CustomerRelationSection({
  relationship,
}: {
  relationship?: CustomerDrawerRelationship;
}) {
  if (!relationship) {
    return (
      <SectionPanel title={detailSectionLabel('customerRelation')}>
        <EmptyBlock description="" />
      </SectionPanel>
    );
  }

  return (
    <>
      <SectionPanel
        title={detailSectionLabel('customerRelation')}
        hint="快速判断关键人覆盖和决策链状态"
      >
        <div className={styles.previewMetricGrid}>
          {[
            {
              key: 'keyContactCount',
              title: '关键联系人数',
              value: `${relationship.healthSummary.keyContactCount} 位`,
              hint: '关键联系人越丰富，推进越稳',
            },
            {
              key: 'roleCoverageCount',
              title: '角色覆盖数',
              value: `${relationship.healthSummary.roleCoverageCount} 类`,
              hint: `决策链状态 ${relationship.healthSummary.decisionChainStatus}`,
            },
            {
              key: 'activeKeyContactCount',
              title: '7天内已触达关键人',
              value: `${relationship.healthSummary.activeKeyContactCount} 位`,
              hint: '看最近推进热度',
            },
            {
              key: 'inactiveKeyContactCount',
              title: '30天未触达关键人',
              value: `${relationship.healthSummary.inactiveKeyContactCount} 位`,
              hint: '优先补联系动作',
            },
          ].map((item) => (
            <div key={item.key} className={styles.previewMetricCard}>
              <div className={styles.previewMetricTitle}>{item.title}</div>
              <div className={styles.previewMetricValue}>{item.value}</div>
              <div className={styles.previewMetricHint}>{item.hint}</div>
            </div>
          ))}
        </div>
      </SectionPanel>

      <SectionPanel title="推进建议" hint="根据当前角色覆盖和触达情况生成">
        <CrmList
          dataSource={relationship.nextSuggestions}
          renderItem={(item) => <CrmList.Item>{item}</CrmList.Item>}
        />
      </SectionPanel>

      <SectionPanel title="决策链视图" hint="按客户内部角色分组查看关系覆盖">
        <div className={styles.relationshipLaneGrid}>
          {relationship.decisionChain.map((group) => (
            <div key={group.role} className={styles.relationshipLane}>
              <div className={styles.relationshipLaneHeader}>
                <div className={styles.relationshipLaneTitle}>
                  {group.label} {group.count ? `(${group.count})` : ''}
                </div>
                {group.hint ? (
                  <div className={styles.relationshipLaneHint}>
                    {group.hint}
                  </div>
                ) : null}
              </div>
              {group.contacts.length ? (
                <div className={styles.relationshipContactList}>
                  {group.contacts.map((contact) => (
                    <div
                      key={contact.uuid as string}
                      className={styles.relationshipContactCard}
                    >
                      <div className={styles.recordTitleRow}>
                        <span className={styles.recordTitle}>
                          {(contact.name as string) || '未命名联系人'}
                        </span>
                        {contact.post ? (
                          <Text type="secondary">{contact.post as string}</Text>
                        ) : null}
                      </div>
                      <Space wrap size={[6, 6]}>
                        {contact.department ? (
                          <Tag>{contact.department as string}</Tag>
                        ) : null}
                        {contact.isPrimaryDecisionMaker ? (
                          <Tag color="green">关键决策人</Tag>
                        ) : null}
                        {((contact.roles as string[]) || []).map((role) => (
                          <Tag key={role} color="blue">
                            {relationshipRoleLabelMap[role] || role}
                          </Tag>
                        ))}
                      </Space>
                      <div className={styles.recordMeta}>
                        <Text type="secondary">
                          直属上级：{(contact.parentName as string) || '-'}
                        </Text>
                        <Text type="secondary">
                          最近联系：
                          {formatCrmDate(
                            contact.contactLastTime as string,
                            'date',
                          )}
                        </Text>
                        <Text type="secondary">
                          下次联系：
                          {formatCrmDate(
                            contact.contactNextTime as string,
                            'date',
                          )}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={false}
                />
              )}
            </div>
          ))}
        </div>
      </SectionPanel>

      <SectionPanel title="组织关系图" hint="基于联系人上下级关系生成树形视图">
        {relationship.orgNodes.length ? (
          <ul className={styles.orgTreeList}>
            {relationship.orgNodes.map((node) => renderOrgNode(node))}
          </ul>
        ) : (
          <EmptyBlock description="" />
        )}
      </SectionPanel>

      <SectionPanel title="异常提醒" hint="帮助发现关系缺口和数据问题">
        {relationship.orgWarnings.length ? (
          <Space orientation="vertical" size={12} style={{ width: '100%' }}>
            {relationship.orgWarnings.map((warning) => (
              <Alert
                key={warning.key}
                type={warning.level === 'danger' ? 'error' : warning.level}
                showIcon
                message={warning.message}
              />
            ))}
          </Space>
        ) : (
          <EmptyBlock description="当前没有发现明显的关系异常" />
        )}
      </SectionPanel>
    </>
  );
}

function TeamMemberSection({
  customerUuid,
  viewMode,
  onRefresh,
}: {
  customerUuid: string;
  viewMode: CrmScopedViewMode;
  onRefresh: () => void;
}) {
  return (
    <TeamMembersPanel
      bizType="customer"
      bizUuid={customerUuid}
      viewMode={viewMode}
      onRefresh={onRefresh}
    />
  );
}

function LeadSection({
  page,
  loading,
  memberNameMap,
  onCreate,
  onEdit,
  onDelete,
  onRefresh,
}: {
  page: CustomerDrawerRelatedPage<LeadItem>;
  loading: boolean;
  memberNameMap: Record<string, string>;
  onCreate: () => void;
  onEdit: (record: LeadItem) => void;
  onDelete: (record: LeadItem) => void;
  onRefresh: () => void;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('leads')}
      hint={pageHint(page, '线索')}
      extra={
        <Space size={4}>
          <Button
            type="link"
            size="small"
            onClick={onRefresh}
            loading={loading}
          >
            刷新
          </Button>
          <Button type="link" size="small" onClick={onCreate}>
            新增线索
          </Button>
        </Space>
      }
    >
      {
        <Table<LeadItem>
          rowKey="uuid"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={page.list}
          columns={[
            {
              title: '线索名称',
              key: 'name',
              render: (_, record) => (
                <LeadReferenceLink
                  leadUuid={record.uuid}
                  leadName={record.name || '未命名线索'}
                />
              ),
            },
            {
              title: '负责人',
              key: 'owner',
              render: (_, record) =>
                resolveMemberText({
                  memberUuid: record.ownerUserUuid,
                  directName:
                    record.ownerUserName ||
                    record.ownerName ||
                    record.ownerMemberName,
                  memberNameMap,
                  emptyText: '未知成员',
                }),
            },
            {
              title: '联系方式',
              key: 'contact',
              render: (_, record) => {
                const parts = [
                  record.mobile ? `手机 ${record.mobile}` : '',
                  record.email ? `邮箱 ${record.email}` : '',
                ].filter(Boolean);
                return parts.length ? parts.join(' / ') : '-';
              },
            },
            {
              title: '跟进状态',
              dataIndex: 'followUpStatus',
              key: 'followUpStatus',
              render: (value?: string) =>
                FOLLOW_UP_STATUS_ENUM[
                  value as unknown as keyof typeof FOLLOW_UP_STATUS_ENUM
                ]?.text ||
                value ||
                '-',
            },
            {
              title: '转化状态',
              dataIndex: 'transformStatus',
              key: 'transformStatus',
              render: (value?: boolean) => (
                <Tag color={value ? 'green' : 'default'}>
                  {value ? '已转化' : '未转化'}
                </Tag>
              ),
            },
            {
              title: '最近联系时间',
              dataIndex: 'contactLastTime',
              key: 'contactLastTime',
              render: (value?: string) => formatCrmDate(value, 'date'),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, record) => (
                <Space size={8}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onEdit(record)}
                  >
                    编辑
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() => onDelete(record)}
                  >
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function OpportunitySection({
  page,
  loading,
  onCreate,
  onEdit,
  onLink,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerOpportunityItem>;
  loading: boolean;
  onCreate: () => void;
  onEdit: (record: CustomerDrawerOpportunityItem) => void;
  onLink: () => void;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('opportunities')}
      hint={pageHint(page, '商机')}
      extra={
        <Space size={8}>
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={onLink}
          >
            关联已有商机
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={onCreate}
          >
            新增商机
          </Button>
        </Space>
      }
    >
      {
        <Table<CustomerDrawerOpportunityItem>
          rowKey="uuid"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={page.list}
          columns={[
            {
              title: '商机名称',
              key: 'name',
              render: (_, record) => {
                const canViewDetail = canReadRecord(record);
                const summaryName =
                  record.name ||
                  (canViewDetail ? '未命名商机' : record.uuid || '未命名商机');
                return (
                  <OpportunityReferenceLink
                    opportunityUuid={record.uuid}
                    opportunityName={summaryName}
                    canViewDetail={canViewDetail}
                  />
                );
              },
            },
            {
              title: '金额',
              dataIndex: 'totalPrice',
              key: 'totalPrice',
              render: (value?: string) =>
                value ? `￥${formatMoney(value)}` : '-',
            },
            {
              title: '结束状态',
              dataIndex: 'endStatus',
              key: 'endStatus',
              render: (value?: number) => statusTag('结束状态', value),
            },
            {
              title: '成交时间',
              dataIndex: 'dealTime',
              key: 'dealTime',
              render: (value?: string) => formatCrmDate(value),
            },
            {
              title: '更新时间',
              dataIndex: 'updatedAt',
              key: 'updatedAt',
              render: (value?: string) => formatCrmDate(value),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, record) =>
                canActOnRecord(record, 'edit') ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onEdit(record)}
                  >
                    编辑
                  </Button>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function QuoteSection({
  page,
  onCreate,
  onEdit,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerQuoteItem>;
  onCreate: () => void;
  onEdit: (record: CustomerDrawerQuoteItem) => void;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('quotes')}
      hint={pageHint(page, '报价单')}
      extra={
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={onCreate}
        >
          新增报价单
        </Button>
      }
    >
      {
        <Table<CustomerDrawerQuoteItem>
          rowKey="uuid"
          size="small"
          pagination={false}
          dataSource={page.list}
          columns={[
            {
              title: '报价单名称',
              key: 'name',
              render: (_, record) => record.name || record.code,
            },
            {
              title: '编号',
              dataIndex: 'code',
              key: 'code',
            },
            {
              title: '金额',
              dataIndex: 'totalPrice',
              key: 'totalPrice',
              render: (value: string) => `￥${formatMoney(value)}`,
            },
            {
              title: '审核状态',
              dataIndex: 'auditStatus',
              key: 'auditStatus',
              render: (value: number) => statusTag('审核', value),
            },
            {
              title: '报价日期',
              key: 'quoteDate',
              render: (_, record) =>
                formatCrmDate(record.quoteDate || record.updatedAt),
            },
            {
              title: '预计成交日期',
              dataIndex: 'expectedDealDate',
              key: 'expectedDealDate',
              render: (value?: string) => formatCrmDate(value),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, record) => (
                <Button type="link" size="small" onClick={() => onEdit(record)}>
                  编辑
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function ContractSection({
  page,
  onCreate,
  onEdit,
  onRefresh,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerContractItem>;
  onCreate: () => void;
  onEdit: (record: CustomerDrawerContractItem) => void;
  onRefresh: () => void;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('contracts')}
      hint={pageHint(page, '合同')}
      extra={
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={onCreate}
        >
          新增合同
        </Button>
      }
    >
      {
        <Table<CustomerDrawerContractItem>
          rowKey="uuid"
          size="small"
          pagination={false}
          dataSource={page.list}
          columns={[
            {
              title: '合同名称',
              key: 'name',
              render: (_, record) => record.name || record.code,
            },
            {
              title: '编号',
              dataIndex: 'code',
              key: 'code',
            },
            {
              title: '金额',
              dataIndex: 'totalPrice',
              key: 'totalPrice',
              render: (value?: string) =>
                value ? `￥${formatMoney(value)}` : '-',
            },
            {
              title: '审核状态',
              key: 'status',
              render: (_, record) => (
                <Tag>{resolveContractDisplayStatusLabel(record)}</Tag>
              ),
            },
            {
              title: '生效时间',
              dataIndex: 'startTime',
              key: 'startTime',
              render: (value?: string) => formatCrmDate(value, 'date'),
            },
            {
              title: '到期时间',
              dataIndex: 'endTime',
              key: 'endTime',
              render: (value?: string) => formatCrmDate(value, 'date'),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, record) => (
                <Space size={8}>
                  {canEditContract(record) ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => onEdit(record)}
                    >
                      编辑
                    </Button>
                  ) : null}
                  <ContractCenterSyncAction
                    contract={record}
                    onSynced={onRefresh}
                  />
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function ProductSection({
  page,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerProductItem>;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('products')}
      hint={pageHint(page, '产品')}
    >
      {page.list.length ? (
        <CrmList
          dataSource={page.list}
          renderItem={(item) => (
            <CrmList.Item>
              <div className={styles.recordBlock}>
                <div className={styles.recordTitleRow}>
                  <span className={styles.recordTitle}>
                    {item.productName || item.productCode || '未命名产品'}
                  </span>
                  <Text type="secondary">{formatCrmDate(item.time)}</Text>
                </div>
                <Space wrap size={[8, 8]}>
                  <Tag color="purple">
                    来源
                    {item.sourceType === 'contract'
                      ? '合同'
                      : item.sourceType === 'quote'
                        ? '报价单'
                        : '商机'}
                  </Tag>
                  {item.sourceCode ? <Tag>{item.sourceCode}</Tag> : null}
                  {item.quantity ? <Tag>数量 {item.quantity}</Tag> : null}
                  {item.totalPrice ? (
                    <Tag color="blue">
                      金额 {formatMoney(item.totalPrice)} 元
                    </Tag>
                  ) : null}
                </Space>
                <div className={styles.recordMeta}>
                  <Text type="secondary">
                    产品编码：{item.productCode || '-'}
                  </Text>
                  <Text type="secondary">
                    来源单据：{item.sourceName || item.sourceCode || '-'}
                  </Text>
                </div>
              </div>
            </CrmList.Item>
          )}
        />
      ) : (
        <EmptyBlock description="" />
      )}
    </SectionPanel>
  );
}

function InvoiceSection({
  page,
  onCreate,
  onEdit,
}: {
  page: CustomerDrawerRelatedPage<CustomerDrawerInvoiceItem>;
  onCreate: () => void;
  onEdit: (record: CustomerDrawerInvoiceItem) => void;
}) {
  const invoiceColumns = buildInvoiceTableColumns<
    CustomerDrawerInvoiceItem & InvoiceTableRecord
  >({
    t: (_, defaultMessage) => defaultMessage,
    onEdit: (record) => onEdit(record),
  });

  return (
    <SectionPanel
      title={detailSectionLabel('invoices')}
      hint={pageHint(page, '发票')}
      extra={
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={onCreate}
        >
          新增发票
        </Button>
      }
    >
      {
        <Table<CustomerDrawerInvoiceItem & InvoiceTableRecord>
          rowKey="uuid"
          size="small"
          pagination={false}
          dataSource={page.list}
          columns={invoiceColumns as any}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}

function AddressSection({
  addresses,
  onCreate,
  onEdit,
  onDelete,
}: {
  addresses: CustomerAddressItem[];
  onCreate: () => void;
  onEdit: (record: CustomerAddressItem) => void;
  onDelete: (record: CustomerAddressItem) => void;
}) {
  const addressTypeLabelMap: Record<
    CustomerAddressItem['addressType'],
    string
  > = {
    office: '办公地址',
    warehouse: '仓储地址',
    registered: '注册地址',
    store: '门店地址',
    home: '家庭地址',
    other: '其它地址',
  };
  const columns: CrmProColumnCompat<CustomerAddressItem>[] = [
    {
      title: '地址类型',
      dataIndex: 'addressType',
      width: 120,
      render: (_, record) =>
        addressTypeLabelMap[record.addressType] || '其它地址',
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      width: 140,
      render: (_, record) => record.contactName || '未设置联系人',
    },
    {
      title: '邮编',
      dataIndex: 'postalCode',
      width: 120,
      render: (_, record) => record.postalCode || '-',
    },
    {
      title: '详细地址',
      dataIndex: 'detailAddress',
      ellipsis: true,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (_, record) => record.remark || '-',
    },
    {
      title: '操作',
      key: 'option',
      valueType: 'option',
      width: 120,
      render: (_, record) => [
        <Button
          key="edit"
          type="link"
          size="small"
          onClick={() => onEdit(record)}
        >
          编辑
        </Button>,
        <Button
          key="delete"
          type="link"
          size="small"
          danger
          onClick={() => onDelete(record)}
        >
          删除
        </Button>,
      ],
    },
  ];

  return (
    <SectionPanel
      title="客户地址"
      extra={
        <Button type="link" icon={<PlusOutlined />} onClick={onCreate}>
          新增地址
        </Button>
      }
    >
      {addresses.length ? (
        <ProTable<CustomerAddressItem>
          rowKey="uuid"
          dataSource={addresses}
          columns={columns}
          size="small"
          search={false}
          pagination={false}
          options={false}
          cardBordered={false}
          toolBarRender={false}
        />
      ) : (
        <EmptyBlock description="" />
      )}
    </SectionPanel>
  );
}

function PendingList({ items }: { items: CustomerDrawerActivity[] }) {
  if (!items.length) {
    return <EmptyBlock description="" />;
  }
  return (
    <CrmList
      dataSource={items}
      renderItem={(item) => (
        <CrmList.Item>
          <div className={styles.recordBlock}>
            <div className={styles.recordTitleRow}>
              <span className={styles.recordTitle}>{item.title}</span>
              <Text type="secondary">
                {formatCrmDate(item.dueTime || item.time)}
              </Text>
            </div>
            <Space wrap size={[8, 8]}>
              {item.amount ? (
                <Tag color="blue">金额 {formatMoney(item.amount)} 元</Tag>
              ) : null}
              {statusTag('状态', item.status)}
            </Space>
            {item.description ? (
              <Paragraph className={styles.recordDescription}>
                {item.description}
              </Paragraph>
            ) : null}
          </div>
        </CrmList.Item>
      )}
    />
  );
}

function UnsupportedSection({
  sectionKey,
}: {
  sectionKey: CustomerDrawerSectionKey;
}) {
  const label =
    SECTION_LABELS.find((section) => section.key === sectionKey)?.label ||
    sectionKey;
  const description =
    SECTION_UNSUPPORTED_COPY[sectionKey] || '当前暂无相关数据。';

  return (
    <SectionPanel title={label}>
      <EmptyBlock description={description} />
    </SectionPanel>
  );
}
